import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  FaUsers, FaPlus, FaEdit, FaTrash, FaSearch, FaUserShield, FaUserTie, 
  FaEye, FaEyeSlash, FaUpload, FaDownload, FaKey, FaCalendarAlt, 
  FaBullseye, FaCog, FaFileExcel, FaShieldAlt, FaLock, FaUnlock,
  FaFilter, FaSort, FaTimes, FaCheck, FaExclamationTriangle,
  FaSpinner, FaSync, FaWifi, FaChevronDown, FaChevronRight,
  FaGraduationCap, FaChartLine, FaClipboardList, FaStar,
  FaBookOpen, FaAward, FaCrown, FaHeart, FaRocket, FaCube,
  FaArrowRight, FaClock, FaSlidersH, FaChalkboardTeacher, FaCheckCircle,
  FaUserPlus, FaUserMinus, FaList, FaTh, FaFileAlt
} from 'react-icons/fa';

import { UserService, User as ServiceUser, UserGoal, PasswordPolicy } from '../services/userService';
import { API_BASE_URL } from '../utils/apiConfig';
import AdvancedGoalManager from './AdvancedGoalManager';
import AdvancedGoalCreationForm from './AdvancedGoalCreationForm';
import AnnualUpdate from './AnnualUpdate';
import BackupManager from './BackupManager';
import Retiro from './Retiro';
import InformeNotasRetiros from './InformeNotasRetiros';
import TeacherReportSettings from './TeacherReportSettings';
import { useTenant } from '../contexts/TenantContext';
// Eliminado del frontend: DimensionUpdater

// Type alias for component compatibility
type User = ServiceUser;

interface Dimension {
  id: number;
  name: string;
  category: string;
}

interface UserManagementProps {
  courses: string[];
  students: any[];
  dimensions: any[];
  username: string;
  onBackupComplete: () => void;
  onDimensionsUpdated: (dimensions: any[]) => void;
}

const UserManagement: React.FC<UserManagementProps> = ({ 
  courses, 
  students: propStudents, 
  dimensions: propDimensions, 
  username: propUsername,
  onBackupComplete,
  onDimensionsUpdated
}) => {
  // Obtener contexto de tenant
  const { tenantKey } = useTenant();
  
  // Filtrar dimensiones según tenant
  const dimensions = useMemo(() => {
    return propDimensions.filter(dimension => {
      // Restringir "Amor a Dios y sentido Cristiano" solo para tenant cpv
      if (dimension.id === 11 && tenantKey !== 'cpv.cl' && tenantKey !== 'cpv_cl') {
        return false;
      }
      return true;
    });
  }, [propDimensions, tenantKey]);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<'all' | 'admin' | 'teacher'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'role' | 'lastLogin' | 'created'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentView, setCurrentView] = useState<'overview' | 'users' | 'estudiantes' | 'informes-retiros' | 'goals' | 'advanced-goals' | 'import' | 'backup' | 'skill-levels' | 'teachers-reports' | 'annual-update'>('users');
  const [usersViewMode, setUsersViewMode] = useState<'list' | 'cards'>('list');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showGoalsModal, setShowGoalsModal] = useState(false);
  const [showQuickGoalsModal, setShowQuickGoalsModal] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'checking'>('checking');
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  
  // Missing state variables for modals
  const [showImportModal, setShowImportModal] = useState(false);
  const [showPasswordPolicyModal, setShowPasswordPolicyModal] = useState(false);
  // const [showDimensionsModal, setShowDimensionsModal] = useState(false);
  
  // Skill Levels Configuration state
  const [skillLevelsConfig, setSkillLevelsConfig] = useState<{[course: string]: number}>({
    'Medio Mayor A': 0,
    'Medio Mayor B': 0,
    'Medio Mayor C': 0,
    'Medio Mayor D': 0,
    'Pre Kinder A': 1,
    'Pre Kinder B': 1,
    'Pre Kinder C': 1,
    'Kinder A': 2,
    'Kinder B': 2,
    'Kinder C': 2,
    'Primero básico A': 3,
    'Primero básico B': 3,
    'Primero básico C': 3,
    'Segundo básico A': 4,
    'Segundo básico B': 4,
    'Segundo básico C': 4,
    'Tercero básico A': 5,
    'Tercero básico B': 5,
    'Tercero básico C': 5,
    'Cuarto básico A': 6,
    'Cuarto básico B': 6,
    'Cuarto básico C': 6,
    'Quinto básico A': 7,
    'Quinto básico B': 7,
    'Quinto básico C': 7,
    'Sexto básico A': 8,
    'Sexto básico B': 8,
    'Sexto básico C': 8,
  });
  const [isApplyingSkillLevels, setIsApplyingSkillLevels] = useState(false);

  // Available dimensions (matching the categories from your app)
  const [allDimensionsList] = useState<Dimension[]>([
    { id: 1, name: "Autoconciencia y voluntad", category: "Exploración de uno mismo" },
    { id: 2, name: "Habilidades pensamiento", category: "Exploración de uno mismo" },
    { id: 3, name: "Habilidades comunicacionales en el lenguaje español", category: "Exploración de uno mismo" },
    { id: 4, name: "Corporalidad y Movimiento", category: "Exploración de uno mismo" },
    { id: 5, name: "Lenguaje Artístico", category: "Exploración de uno mismo" },
    { id: 6, name: "Motivación y disposición hacia el aprendizaje", category: "Exploración de uno mismo" },
    { id: 7, name: "Encuentro con la Naturaleza y Sociedad", category: "Exploración del mundo" },
    { id: 8, name: "Habilidades de relación y conciencia social", category: "Exploración del mundo" },
    { id: 9, name: "Pensamiento matemático", category: "Exploración del mundo" },
    { id: 10, name: "Acercamiento a la lengua inglesa", category: "Exploración del mundo" },
    { id: 11, name: "Amor a Dios y sentido Cristiano", category: "Exploración de lo Trascendente" },
    { id: 12, name: "Trabajo colaborativo", category: "Descripción Cualitativa" }
  ]);

  // Password policy
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy>({
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: false,
    expirationDays: 90,
    preventReuse: 5,
    maxFailedAttempts: 5,
    lockoutDuration: 30
  });

  // Form data
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    role: 'teacher' as 'admin' | 'teacher',
    allowedCourses: [] as string[],
    allowedDimensions: [] as number[],
    isActive: true,
    mustChangePassword: true,
  });

  // Goal form data
  const [goalFormData, setGoalFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    targetDate: '',
    priority: 'medium' as 'low' | 'medium' | 'high'
  });

  // Wizard steps state
  const [currentStep, setCurrentStep] = useState(1);
  const [stepValidation, setStepValidation] = useState({
    step1: false,
    step2: false,
    step3: false
  });

  // KPIs del sistema (desde MongoDB)
  const [realTimeKPIs, setRealTimeKPIs] = useState<any>(null);
  const [showUserSelectionModal, setShowUserSelectionModal] = useState(false);
  const [selectedUsersForGoal, setSelectedUsersForGoal] = useState<string[]>([]);
  const [currentGoalData, setCurrentGoalData] = useState<any>(null);
  const [creationProgress, setCreationProgress] = useState<{
    total: number;
    completed: number;
    currentUser: string;
  } | null>(null);

  // Estados para edición de metas
  const [showEditGoalModal, setShowEditGoalModal] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<UserGoal | null>(null);
  const [selectedGoalUser, setSelectedGoalUser] = useState<User | null>(null);
  const [editGoalFormData, setEditGoalFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    targetDate: '',
    priority: 'medium' as 'low' | 'medium' | 'high',
    status: 'pending' as 'pending' | 'in_progress' | 'completed' | 'overdue',
    progress: 0
  });

  // Estados para eliminación masiva de metas
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [usersSelectedForBulkDelete, setUsersSelectedForBulkDelete] = useState<string[]>([]);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState<{
    total: number;
    completed: number;
    currentUser: string;
  } | null>(null);

  // Tab para Actualización Anual (Docentes vs Estudiantes)
  const [annualUpdateTab, setAnnualUpdateTab] = useState<'teachers' | 'students'>('teachers');

  // Estados para Actualización Anual de Docentes
  const [annualUpdateStep, setAnnualUpdateStep] = useState<'init' | 'upload' | 'review' | 'confirm' | 'processing' | 'complete'>('init');
  const [backupData, setBackupData] = useState<any>(null);
  const [newUsersData, setNewUsersData] = useState<any[]>([]);
  const [usersToDeactivate, setUsersToDeactivate] = useState<User[]>([]);
  const [usersToMaintain, setUsersToMaintain] = useState<User[]>([]);
  const [usersToCreate, setUsersToCreate] = useState<any[]>([]);
  const [usersToUpdate, setUsersToUpdate] = useState<{user: User, newData: any, changes: string[]}[]>([]);
  const [annualUpdateProgress, setAnnualUpdateProgress] = useState<{
    phase: string;
    current: number;
    total: number;
    currentItem: string;
    errors: string[];
  } | null>(null);
  const [annualUpdateConfig, setAnnualUpdateConfig] = useState({
    deactivateOldUsers: true,
    deleteOldUsers: false,
    preserveAdmins: true,
    sendWelcomeEmails: false
  });

  // Estados para Actualización Anual de Estudiantes
  const [studentAnnualUpdateStep, setStudentAnnualUpdateStep] = useState<'init' | 'upload' | 'review' | 'confirm' | 'processing' | 'complete'>('init');
  const [selectedAcademicYear, setSelectedAcademicYear] = useState<string>("2025");
  const [studentNewData, setStudentNewData] = useState<any[]>([]);
  const [studentsToUpdate, setStudentsToUpdate] = useState<any[]>([]);
  const [studentsToCreate, setStudentsToCreate] = useState<any[]>([]);
  const [studentsToWithdraw, setStudentsToWithdraw] = useState<any[]>([]);
  const [studentAnnualUpdateProgress, setStudentAnnualUpdateProgress] = useState<{
    phase: string;
    current: number;
    total: number;
    currentItem: string;
    errors: string[];
  } | null>(null);

  // Función para cargar KPIs reales desde MongoDB
  const loadRealTimeKPIs = async () => {
    try {
      const API_BASE_URL = process.env.NODE_ENV === 'production'
        ? ''
        : 'http://localhost:5000';
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/kpis`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRealTimeKPIs(data.kpis);
      }
    } catch (error) {
      console.error('Error cargando KPIs:', error);
    }
  };



  // KPIs del sistema (usar datos reales si están disponibles)
  const getSystemKPIs = () => {
    if (realTimeKPIs) {
      return {
        dataInputProgress: {
          current: realTimeKPIs.dataInputProgress.current,
          target: realTimeKPIs.dataInputProgress.target,
          expected: Math.round((new Date().getDate() / new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate()) * 100),
          completed: realTimeKPIs.dataInputProgress.completed,
          total: realTimeKPIs.dataInputProgress.total,
          label: '% Ingreso de Datos'
        },
        skillAssessmentCompletion: {
          current: realTimeKPIs.skillAssessmentCompletion.current,
          target: realTimeKPIs.skillAssessmentCompletion.target,
          trend: realTimeKPIs.skillAssessmentCompletion.trend || '+0%',
          completed: realTimeKPIs.skillAssessmentCompletion.completed,
          total: realTimeKPIs.skillAssessmentCompletion.total,
          label: 'Evaluaciones Completadas'
        },
        userEngagement: {
          current: realTimeKPIs.userEngagement.current,
          target: realTimeKPIs.userEngagement.target,
          active: realTimeKPIs.userEngagement.active,
          total: realTimeKPIs.userEngagement.total,
          label: 'Participación Activa'
        },
        dataQuality: {
          current: realTimeKPIs.dataQuality.current,
          target: realTimeKPIs.dataQuality.target,
          complete: realTimeKPIs.dataQuality.complete,
          total: realTimeKPIs.dataQuality.total,
          label: 'Calidad de Datos'
        }
      };
    }

    // Fallback a datos simulados si no hay datos reales
    const totalUsers = users.length;
    const activeUsers = users.filter(u => u.isActive !== false).length;
    
    return {
      dataInputProgress: {
        current: 73,
        target: 85,
        expected: Math.round((new Date().getDate() / new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate()) * 100),
        completed: 307,
        total: 420,
        label: '% Ingreso de Datos'
      },
      skillAssessmentCompletion: {
        current: 68,
        target: 90,
        trend: '+5%',
        completed: 307,
        total: 420,
        label: 'Evaluaciones Completadas'
      },
      userEngagement: {
        current: Math.round((activeUsers / totalUsers) * 100),
        target: 95,
        active: activeUsers,
        total: totalUsers,
        label: 'Participación Activa'
      },
      dataQuality: {
        current: 85,
        target: 95,
        complete: 307,
        total: 420,
        label: 'Calidad de Datos'
      }
    };
  };

  // Usar useMemo para evitar recalcular KPIs en cada render
  const kpis = useMemo(() => getSystemKPIs(), [getSystemKPIs]);





  // Función para crear metas rápidas
  const createQuickGoal = (type: string) => {
    if (type === 'monthly-data-target') {
      setGoalFormData({
        title: `Alcanzar ${kpis.dataInputProgress.target}% de ingreso de datos - ${new Date().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}`,
        description: `Meta para completar el ${kpis.dataInputProgress.target}% del ingreso de datos antes del fin de mes. Actualmente en ${kpis.dataInputProgress.current}%.`,
        startDate: new Date().toISOString().split('T')[0],
        targetDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
        priority: 'high' as 'low' | 'medium' | 'high'
      });
      setShowGoalsModal(true);
    }
  };

  // Función para asignar meta sugerida a usuarios
  const assignSuggestedGoal = (suggestion: any) => {
    setCurrentGoalData(suggestion);
    setSelectedUsersForGoal([]);
    setShowUserSelectionModal(true);
  };

  // Función para crear meta para usuarios seleccionados con mejor manejo de errores
  const createGoalForSelectedUsers = async (goalData?: any) => {
    const dataToUse = goalData || currentGoalData;
    
    // NUEVO DEBUG TRACKING v2.0 - 2025-06-07 22:25
    console.log('🔍 createGoalForSelectedUsers NUEVA VERSION v2.0:', {
      goalDataParam: goalData,
      currentGoalData,
      dataToUse,
      selectedUsersCount: selectedUsersForGoal.length,
      hasData: !!dataToUse,
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack?.split('\n').slice(1, 5).join(' -> ')
    });
    
    if (!dataToUse || selectedUsersForGoal.length === 0) {
      console.error('❌ NUEVA VERSION v2.0 - Missing data validation:', { 
        hasGoalData: !!dataToUse, 
        selectedUsersCount: selectedUsersForGoal.length,
        dataToUse,
        currentGoalData,
        caller: new Error().stack?.split('\n')[2]?.trim()
      });
      showError('No hay datos de meta o usuarios seleccionados');
      return;
    }

    try {
      setLoading(true);
      setCreationProgress({
        total: selectedUsersForGoal.length,
        completed: 0,
        currentUser: ''
      });
      
      // Crear metas individuales para cada usuario con throttling para evitar sobrecargar el servidor
      let successCount = 0;
      let errorCount = 0;
      const detailedResults = [];
      const failedUsers = [];
      
      for (let i = 0; i < selectedUsersForGoal.length; i++) {
        const userId = selectedUsersForGoal[i];
        
        try {
          const user = users.find(u => getUserId(u) === userId);
          if (!user) {
            errorCount++;
            setCreationProgress(prev => prev ? { ...prev, completed: prev.completed + 1 } : null);
            continue;
          }
          
          // Actualizar progreso
          setCreationProgress(prev => prev ? { 
            ...prev, 
            completed: i,
            currentUser: `${user.firstName} ${user.lastName}`
          } : null);
          
          const goalPayload = {
            title: dataToUse.title,
            description: dataToUse.description,
            targetDate: dataToUse.targetDate,
            priority: dataToUse.priority,
            status: 'pending' as const,
            progress: 0,
            startDate: dataToUse.startDate
          };
          
          // Usar el servicio UserService para agregar la meta
          const updatedUser = await UserService.addGoal(userId, goalPayload);
          
          // Actualizar la lista local de usuarios inmediatamente
          setUsers(prev => {
            const newUsers = prev
              .filter(u => u !== undefined && u !== null) // 🛡️ Filtrar elementos undefined/null
              .map(u => 
                getUserId(u) === userId ? updatedUser : u
              );
            console.log(`📝 Usuario ${updatedUser.firstName} ${updatedUser.lastName} actualizado:`, {
              userId,
              goalsCount: updatedUser.goals?.length || 0,
              newGoal: updatedUser.goals?.[updatedUser.goals.length - 1] || 'No goals'
            });
            return newUsers;
          });
          
          detailedResults.push({
            userId,
            userName: `${user.firstName} ${user.lastName}`,
            status: 'success'
          });
          
          successCount++;
          
          // Agregar pequeño delay para evitar sobrecargar el servidor (cada 5 requests)
          if ((i + 1) % 5 === 0 && i < selectedUsersForGoal.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
        } catch (error: any) {
          const user = users.find(u => getUserId(u) === userId);
          const userName = user ? `${user.firstName} ${user.lastName}` : 'Usuario desconocido';
          
          failedUsers.push({
            userId,
            userName,
            error: error.response?.data?.message || error.message || 'Error desconocido'
          });
          
          detailedResults.push({
            userId,
            userName,
            status: 'error',
            error: error.response?.data?.message || error.message
          });
          
          errorCount++;
          
          // Si hay muchos errores consecutivos (posiblemente servidor sobrecargado), hacer pausa más larga
          if (errorCount > 3 && errorCount % 3 === 0) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
        
        // Actualizar progreso completado
        setCreationProgress(prev => prev ? { ...prev, completed: i + 1 } : null);
      }
      
      // Mostrar resultados detallados
      if (successCount > 0) {
        let message = `Meta "${dataToUse.title}" asignada exitosamente a ${successCount} usuario(s)`;
        
        if (errorCount > 0) {
          message += `. ${errorCount} usuario(s) fallaron`;
          

        }
        
        showSuccess(message);
        
        // Recargar usuarios para ver las metas actualizadas
        console.log('🔄 Recargando usuarios después de crear metas...');
        await loadUsers();
      } else {
        let errorMessage = 'No se pudo asignar la meta a ningún usuario';
        
        if (failedUsers.length > 0) {
          const commonErrors = failedUsers.reduce((acc, user) => {
            acc[user.error] = (acc[user.error] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);
          
          const mostCommonError = Object.entries(commonErrors)
            .sort(([,a], [,b]) => (b as number) - (a as number))[0];
          
          if (mostCommonError) {
            errorMessage += `. Error más común: ${mostCommonError[0]} (${mostCommonError[1]} usuarios)`;
          }
        }
        
        showError(errorMessage);
      }
      
      // Limpiar y cerrar modales
      setShowUserSelectionModal(false);
      setShowGoalsModal(false);
      setCurrentGoalData(null);
      setSelectedUsersForGoal([]);
      resetWizard();
      
    } catch (error: any) {
      showError(`Error general al crear metas: ${error.message || 'Error desconocido'}`);
    } finally {
      setLoading(false);
      setCreationProgress(null);
    }
  };

  // Función para alternar selección de usuario
  const toggleUserSelection = (userId: string) => {
    setSelectedUsersForGoal(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Función para seleccionar todos los usuarios
  const selectAllUsers = () => {
    setSelectedUsersForGoal(
      users
        .filter(user => user !== undefined && user !== null) // 🛡️ Filtrar elementos undefined/null
        .map(user => getUserId(user))
    );
  };

  // Función para deseleccionar todos
  const deselectAllUsers = () => {
    setSelectedUsersForGoal([]);
  };

  // Funciones para el wizard de creación de metas
  const validateStep1 = () => {
    return goalFormData.title.trim() !== '' && 
           goalFormData.startDate !== '' && 
           goalFormData.targetDate !== '' &&
           new Date(goalFormData.startDate) <= new Date(goalFormData.targetDate);
  };

  const validateStep2 = () => {
    return selectedUsersForGoal.length > 0;
  };

  const goToNextStep = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
      setStepValidation(prev => ({ ...prev, step1: true }));
    } else if (currentStep === 2 && validateStep2()) {
      setCurrentStep(3);
      setStepValidation(prev => ({ ...prev, step2: true }));
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const resetWizard = () => {
    setCurrentStep(1);
    setSelectedUsersForGoal([]);
    setGoalFormData({
      title: '',
      description: '',
      startDate: '',
      targetDate: '',
      priority: 'medium'
    });
    setStepValidation({
      step1: false,
      step2: false,
      step3: false
    });
  };

  const handleCloseGoalModal = () => {
    setShowGoalsModal(false);
    resetWizard();
  };

  // Load users from MongoDB
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Cargando usuarios desde MongoDB...');
      
      const fetchedUsers = await UserService.getAllUsers();
      console.log('📥 Usuarios recibidos en UserManagement:', {
        count: fetchedUsers.length,
        users: fetchedUsers.map(u => u?.username || 'undefined')
      });
      
      // 🛡️ Filtrar usuarios undefined/null antes de guardar en estado
      const validUsers = fetchedUsers.filter(u => u !== undefined && u !== null && u._id);
      if (validUsers.length !== fetchedUsers.length) {
        console.warn(`⚠️ Se filtraron ${fetchedUsers.length - validUsers.length} usuarios inválidos`);
      }
      
      setUsers(validUsers);
      setConnectionStatus('connected');
    } catch (error: any) {
      console.error('❌ Error loading users:', error);
      setError(error.message || 'Error al cargar usuarios');
      setConnectionStatus('disconnected');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load password policy
  const loadPasswordPolicy = useCallback(async () => {
    try {
      const policy = await UserService.getPasswordPolicy();
      setPasswordPolicy(policy);
    } catch (error) {
      console.error('Error loading password policy:', error);
      // Keep default policy if error
    }
  }, []);

  // Check connection status
  const checkConnection = useCallback(async () => {
    try {
      setConnectionStatus('checking');
      const isConnected = await UserService.checkConnection();
      setConnectionStatus(isConnected ? 'connected' : 'disconnected');
    } catch (error) {
      setConnectionStatus('disconnected');
    }
  }, []);

  // Initialize authentication token
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    console.log('🔐 UserManagement - Token obtenido:', storedToken ? 'Token disponible' : 'No token');
    setAuthToken(storedToken);
  }, []);

  // Initialize component
  useEffect(() => {
    loadUsers();
    loadPasswordPolicy();
    checkConnection();
    
    // Load KPIs in background, don't block UI
    setTimeout(() => {
      loadRealTimeKPIs();
    }, 1000);
  }, [loadUsers, loadPasswordPolicy, checkConnection]);

  // 🛡️ SALVAGUARDA: Limpiar usuarios undefined/null automáticamente
  useEffect(() => {
    const validUsers = users.filter(u => u !== undefined && u !== null && u._id);
    if (validUsers.length !== users.length && users.length > 0) {
      console.warn(`⚠️ SALVAGUARDA: Limpiando ${users.length - validUsers.length} usuarios inválidos automáticamente`);
      setUsers(validUsers);
    }
  }, [users]);

  // Refresh KPIs every 5 minutes (not 2 minutes to reduce load)
  useEffect(() => {
    const interval = setInterval(() => {
      loadRealTimeKPIs();
    }, 300000); // 5 minutes
    
    return () => clearInterval(interval);
  }, []);

  // Clear messages after delay
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Filter and sort users
  useEffect(() => {
    const userList = users || [];
    console.log('Filtrando usuarios. Total en estado:', userList.length);
    let filtered = userList.filter(user => {
      // 🛡️ VALIDACIÓN: Filtrar elementos undefined/null antes de acceder a propiedades
      if (!user) {
        console.warn('⚠️ Usuario undefined encontrado en lista, omitiendo...');
        return false;
      }
      
      const matchesSearch = (user.username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (user.firstName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (user.lastName || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesRole = filterRole === 'all' || user.role === filterRole;
      const matchesStatus = filterStatus === 'all' || 
                           (filterStatus === 'active' && user.isActive !== false) ||
                           (filterStatus === 'inactive' && user.isActive === false);
      
      return matchesSearch && matchesRole && matchesStatus;
    });

    console.log('Usuarios después del filtro:', filtered.length);

    // Sort users
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(`${b.firstName || ''} ${b.lastName || ''}`);
          break;
        case 'role':
          comparison = (a.role || '').localeCompare(b.role || '');
          break;
        case 'lastLogin':
          comparison = (a.lastLogin || '').localeCompare(b.lastLogin || '');
          break;
        case 'created':
          comparison = (a.createdAt || '').localeCompare(b.createdAt || '');
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    // Quienes pidieron recuperar la clave van primero, para que el administrador los vea
    filtered.sort((a, b) => Number(!!b.passwordResetRequestedAt) - Number(!!a.passwordResetRequestedAt));

    setFilteredUsers(filtered);
    console.log('Usuarios finales para renderizar:', filtered.length);
  }, [users, searchTerm, filterRole, filterStatus, sortBy, sortOrder]);

  // Function to convert course names to compact format
  const getCompactCourseName = (courseName: string): string => {
    // Handle numbered grades with sections (check these first for more specific matches)
    const patterns = [
      // Handle preschool with sections (Medio Mayor, Pre Kinder, Kinder)
      { regex: /medio\s*mayor\s+([a-z])/i, replacement: 'MM$1' },
      { regex: /pre\s*kinder\s+([a-z])/i, replacement: 'PK$1' },
      { regex: /prekinder\s+([a-z])/i, replacement: 'PK$1' },
      { regex: /^kinder\s+([a-z])/i, replacement: 'KK$1' },
      // Handle basic grades
      { regex: /primero\s+b[aá]sico\s+([a-z])/i, replacement: '1°$1' },
      { regex: /segundo\s+b[aá]sico\s+([a-z])/i, replacement: '2°$1' },
      { regex: /tercero\s+b[aá]sico\s+([a-z])/i, replacement: '3°$1' },
      { regex: /cuarto\s+b[aá]sico\s+([a-z])/i, replacement: '4°$1' },
      { regex: /quinto\s+b[aá]sico\s+([a-z])/i, replacement: '5°$1' },
      { regex: /sexto\s+b[aá]sico\s+([a-z])/i, replacement: '6°$1' },
      { regex: /s[eé]ptimo\s+b[aá]sico\s+([a-z])/i, replacement: '7°$1' },
      { regex: /octavo\s+b[aá]sico\s+([a-z])/i, replacement: '8°$1' },
      // Handle middle school
      { regex: /primero\s+medio\s+([a-z])/i, replacement: '1°M$1' },
      { regex: /segundo\s+medio\s+([a-z])/i, replacement: '2°M$1' },
      { regex: /tercero\s+medio\s+([a-z])/i, replacement: '3°M$1' },
      { regex: /cuarto\s+medio\s+([a-z])/i, replacement: '4°M$1' },
      // Handle numeric formats
      { regex: /(\d+)[°º]?\s*b[aá]sico\s+([a-z])/i, replacement: '$1°$2' },
      { regex: /(\d+)[°º]?\s*medio\s+([a-z])/i, replacement: '$1°M$2' },
    ];
    
    for (const pattern of patterns) {
      if (pattern.regex.test(courseName)) {
        return courseName.replace(pattern.regex, pattern.replacement).toUpperCase();
      }
    }
    
    // Handle special cases without section letters (fallback)
    if (courseName.toLowerCase().includes('medio mayor')) return 'MM';
    if (courseName.toLowerCase().includes('pre kinder') || courseName.toLowerCase().includes('prekinder')) return 'PK';
    if (courseName.toLowerCase().includes('kinder') && !courseName.toLowerCase().includes('pre')) return 'KK';

    // If no pattern matches, return first 3 characters + last character if long
    if (courseName.length > 4) {
      const words = courseName.split(' ');
      if (words.length > 1) {
        return words.map(word => word.charAt(0)).join('').toUpperCase();
      }
      return courseName.substring(0, 3).toUpperCase();
    }
    
    return courseName.toUpperCase();
  };

  // Helper functions
  const getUserId = (user: User | undefined): string => {
    if (!user) {
      console.warn('⚠️ getUserId llamado con usuario undefined');
      return '';
    }
    return user._id || '';
  };

  const showSuccess = (message: string) => {
    setSuccess(message);
    setError(null);
  };

  const showError = (message: string) => {
    setError(message);
    setSuccess(null);
  };

  const getUserStats = () => {
    // Ensure users is an array and filter out undefined/null values
    const userList = (users || []).filter(u => u !== undefined && u !== null);
    
    const totalUsers = userList.length;
    const activeUsers = userList.filter(u => u.isActive !== false).length; // Default to active if undefined
    const adminUsers = userList.filter(u => u.role === 'admin').length;
    const teacherUsers = userList.filter(u => u.role === 'teacher').length;
    const lockedUsers = userList.filter(u => u.lockedUntil && new Date(u.lockedUntil) > new Date()).length;
    const usersWithGoals = userList.filter(u => u.goals && Array.isArray(u.goals) && u.goals.length > 0).length;
    
    return { totalUsers, activeUsers, adminUsers, teacherUsers, lockedUsers, usersWithGoals };
  };

  const handleCreateUser = () => {
    setFormData({
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
      role: 'teacher',
      allowedCourses: [],
      allowedDimensions: [],
      isActive: true,
      mustChangePassword: true
    });
    setSelectedUser(null);
    setModalError(null);
    setShowCreateModal(true);
  };

  const handleEditUser = (user: User) => {
    console.log('🔧 DEBUG handleEditUser:', {
      user: user,
      userRole: user.role,
      userAllowedDimensions: user.allowedDimensions,
      dimensionsArray: dimensions,
      dimensionsLength: dimensions.length
    });

    // Convertir allowedDimensions a números para asegurar comparación correcta con dimension.id
    const dimensionsAsNumbers = (user.allowedDimensions || []).map((d: any) => 
      typeof d === 'string' ? parseInt(d, 10) : Number(d)
    ).filter((d: number) => !isNaN(d));
    
    const newFormData = {
      username: user.username || '',
      email: user.email || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      password: '',
      confirmPassword: '',
      role: user.role || 'teacher',
      allowedCourses: user.allowedCourses || [],
      allowedDimensions: dimensionsAsNumbers,
      isActive: user.isActive !== false, // Default to true if undefined
      mustChangePassword: user.mustChangePassword || false
    };
    setModalError(null);

    console.log('🔧 DEBUG newFormData:', newFormData);
    console.log('🔧 DEBUG dimensions disponibles:', dimensions);

    setFormData(newFormData);
    setSelectedUser(user);
    setShowEditModal(true);
  };

  // Función para manejar cambios en cursos permitidos
  const handleCourseChange = (course: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      allowedCourses: checked 
        ? [...prev.allowedCourses, course]
        : prev.allowedCourses.filter(c => c !== course)
    }));
  };

  // Función para manejar cambios en dimensiones permitidas
  const handleDimensionChange = (dimensionId: number, checked: boolean) => {
    const numDimensionId = Number(dimensionId);
    setFormData(prev => ({
      ...prev,
      allowedDimensions: checked 
        ? [...prev.allowedDimensions.filter(id => Number(id) !== numDimensionId), numDimensionId]
        : prev.allowedDimensions.filter(id => Number(id) !== numDimensionId)
    }));
  };

  // Función para guardar usuario (crear o editar)
  const handleSaveUser = async () => {
    try {
      setLoading(true);
      setModalError(null);
      
      // Validaciones
      if (!formData.username || !formData.email || !formData.firstName || !formData.lastName) {
        setModalError('Por favor completa todos los campos requeridos');
        return;
      }
      
      if (!selectedUser && (!formData.password || formData.password !== formData.confirmPassword)) {
        setModalError('La contraseña es requerida y debe coincidir');
        return;
      }
      
      if (selectedUser) {
        // Validar contraseña si se proporcionó
        if (formData.password && formData.password !== formData.confirmPassword) {
          setModalError('Las contraseñas no coinciden');
          return;
        }
        
        // Actualizar usuario existente
        const updatedUser = await UserService.updateUser(getUserId(selectedUser), {
          username: formData.username,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          allowedCourses: formData.allowedCourses,
          allowedDimensions: formData.allowedDimensions,
          isActive: formData.isActive,
          mustChangePassword: formData.mustChangePassword
        });
        
        // Si se proporcionó una nueva contraseña, cambiarla
        let finalUser = updatedUser;
        if (formData.password && formData.password.trim() !== '') {
          try {
            finalUser = await UserService.changePassword(getUserId(selectedUser), formData.password);
            showSuccess('Usuario y contraseña actualizados correctamente');
          } catch (passwordError: any) {
            setModalError(`Usuario actualizado, pero error al cambiar contraseña: ${passwordError.message}`);
          }
        } else {
          showSuccess('Usuario actualizado correctamente');
        }
        
        // Siempre recargar la lista completa para asegurar datos actualizados
        await loadUsers();
      } else {
        // Crear nuevo usuario
        const newUser = await UserService.createUser({
          username: formData.username,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          password: formData.password,
          role: formData.role,
          allowedCourses: formData.allowedCourses,
          allowedDimensions: formData.allowedDimensions,
          isActive: formData.isActive,
          mustChangePassword: formData.mustChangePassword
        });
        
        // Agregar a la lista local
        if (newUser) { // 🛡️ Validar que newUser no sea undefined
          setUsers(prev => [...prev, newUser]);
        } else {
          console.warn('⚠️ Intento de agregar usuario undefined, operación omitida');
        }
        
        showSuccess('Usuario creado correctamente');
      }
      
      // Cerrar modal
      setShowCreateModal(false);
      setShowEditModal(false);
      setSelectedUser(null);
      setModalError(null);
      
    } catch (error: any) {
      setModalError(error.message || 'Error al guardar usuario');
    } finally {
      setLoading(false);
    }
  };

  // Función para resetear contraseña
  const handleResetPassword = async (userId: string) => {
    if (!window.confirm('¿Estás seguro de que quieres resetear la contraseña de este usuario?')) {
      return;
    }
    
    try {
      setLoading(true);
      const result = await UserService.resetPassword(userId);
      showSuccess(`Contraseña reseteada. Nueva contraseña: ${result.newPassword}`);
      // prompt deja seleccionar y copiar la clave para entregarla al usuario
      window.prompt('Clave temporal generada. Cópiala y entrégasela al usuario:', result.newPassword);
      
      // Recargar usuarios
      await loadUsers();
    } catch (error: any) {
      showError(error.message || 'Error al resetear contraseña');
    } finally {
      setLoading(false);
    }
  };

  // Función para cambiar estado de usuario
  const handleToggleUserStatus = async (userId: string, currentStatus: boolean) => {
    try {
      setLoading(true);
      const updatedUser = await UserService.toggleUserStatus(userId, !currentStatus);
      
      // Actualizar lista local - verificar que updatedUser no sea undefined
      if (updatedUser) {
        setUsers(prev => prev
          .filter(u => u !== undefined && u !== null) // 🛡️ Filtrar elementos undefined/null
          .map(u => 
            getUserId(u) === userId ? updatedUser : u
          )
        );
      } else {
        await loadUsers();
      }
      
      showSuccess(`Usuario ${!currentStatus ? 'activado' : 'desactivado'} correctamente`);
    } catch (error: any) {
      showError(error.message || 'Error al cambiar estado del usuario');
    } finally {
      setLoading(false);
    }
  };

  // Función para eliminar usuario
  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este usuario? Esta acción no se puede deshacer.')) {
      return;
    }
    
    try {
      setLoading(true);
      await UserService.deleteUser(userId);
      
      // Remover de la lista local
      setUsers(prev => prev.filter(u => u !== undefined && u !== null && getUserId(u) !== userId));
      
      showSuccess('Usuario eliminado correctamente');
    } catch (error: any) {
      showError(error.message || 'Error al eliminar usuario');
    } finally {
      setLoading(false);
    }
  };

  // Función para gestionar metas
  const handleManageGoals = (user: User) => {
    setSelectedUser(user);
    setShowGoalsModal(true);
  };

  // Función para agregar meta
  const handleAddGoal = async () => {
    try {
      if (!selectedUser) return;
      
      setLoading(true);
      const updatedUser = await UserService.addGoal(getUserId(selectedUser), {
        title: goalFormData.title,
        description: goalFormData.description,
        targetDate: goalFormData.targetDate,
        priority: goalFormData.priority,
        status: 'pending',
        progress: 0,
        startDate: goalFormData.startDate
      });
      
      // Actualizar lista local - verificar que updatedUser no sea undefined
      if (updatedUser) {
        setUsers(prev => prev
          .filter(u => u !== undefined && u !== null) // 🛡️ Filtrar elementos undefined/null
          .map(u => 
            getUserId(u) === getUserId(selectedUser) ? updatedUser : u
          )
        );
      }
      
      // Resetear formulario
      setGoalFormData({
        title: '',
        description: '',
        startDate: '',
        targetDate: '',
        priority: 'medium'
      });
      
      showSuccess('Meta agregada correctamente');
    } catch (error: any) {
      showError(error.message || 'Error al agregar meta');
    } finally {
      setLoading(false);
    }
  };

  // Función para actualizar progreso de meta
  const handleUpdateGoalProgress = async (goalId: number, progress: number) => {
    try {
      if (!selectedUser) return;
      
      setLoading(true);
      const updatedUser = await UserService.updateGoalProgress(getUserId(selectedUser), goalId, progress);
      
      // Actualizar lista local - verificar que updatedUser no sea undefined
      if (updatedUser) {
        setUsers(prev => prev
          .filter(u => u !== undefined && u !== null) // 🛡️ Filtrar elementos undefined/null
          .map(u => 
            getUserId(u) === getUserId(selectedUser) ? updatedUser : u
          )
        );
      }
      
      showSuccess('Progreso actualizado correctamente');
    } catch (error: any) {
      showError(error.message || 'Error al actualizar progreso');
    } finally {
      setLoading(false);
    }
  };

  // Función para eliminar meta con confirmación mejorada
  const handleDeleteGoal = async (goalId: number, goalTitle?: string, userToUpdate?: User) => {
    const targetUser = userToUpdate || selectedUser;
    if (!targetUser) return;

    // Confirmación más informativa
    const goalName = goalTitle || 'esta meta';
    const userName = `${targetUser.firstName} ${targetUser.lastName}`;
    
    if (!window.confirm(
      `¿Estás seguro de que quieres eliminar "${goalName}" del usuario ${userName}?\n\n` +
      'Esta acción no se puede deshacer.'
    )) {
      return;
    }
    
    try {
      setLoading(true);
      const updatedUser = await UserService.deleteGoal(getUserId(targetUser), goalId);
      
      // Actualizar lista local - verificar que updatedUser no sea undefined
      if (updatedUser) {
        setUsers(prev => prev
          .filter(u => u !== undefined && u !== null) // 🛡️ Filtrar elementos undefined/null
          .map(u => 
            getUserId(u) === getUserId(targetUser) ? updatedUser : u
          )
        );
      }
      
      showSuccess(`Meta "${goalName}" eliminada correctamente de ${userName}`);
    } catch (error: any) {
      showError(error.message || 'Error al eliminar meta');
    } finally {
      setLoading(false);
    }
  };

  // Función para abrir modal de edición de meta
  const handleEditGoal = (goal: UserGoal, user: User) => {
    setSelectedGoal(goal);
    setSelectedGoalUser(user);
    setEditGoalFormData({
      title: goal.title,
      description: goal.description || '',
      startDate: goal.startDate ? new Date(goal.startDate).toISOString().split('T')[0] : '',
      targetDate: new Date(goal.targetDate).toISOString().split('T')[0],
      priority: goal.priority,
      status: goal.status,
      progress: goal.progress
    });
    setShowEditGoalModal(true);
  };

  // Función para guardar cambios de meta editada
  const handleSaveEditedGoal = async () => {
    if (!selectedGoal || !selectedGoalUser) return;

    try {
      setLoading(true);
      
      // Validar datos básicos
      if (!editGoalFormData.title.trim() || !editGoalFormData.targetDate) {
        showError('El título y la fecha límite son requeridos');
        return;
      }

      // Validar fechas
      if (editGoalFormData.startDate && editGoalFormData.targetDate) {
        if (new Date(editGoalFormData.startDate) > new Date(editGoalFormData.targetDate)) {
          showError('La fecha de inicio debe ser anterior a la fecha límite');
          return;
        }
      }

      const goalPayload = {
        title: editGoalFormData.title.trim(),
        description: editGoalFormData.description.trim(),
        targetDate: editGoalFormData.targetDate,
        priority: editGoalFormData.priority,
        status: editGoalFormData.status,
        progress: editGoalFormData.progress,
        ...(editGoalFormData.startDate && { startDate: editGoalFormData.startDate })
      };

      // Actualizar la meta usando el servicio
      const updatedUser = await UserService.updateGoal(getUserId(selectedGoalUser), selectedGoal.id, goalPayload);
      
      // Actualizar lista local - verificar que updatedUser no sea undefined
      if (updatedUser) {
        setUsers(prev => prev
          .filter(u => u !== undefined && u !== null) // 🛡️ Filtrar elementos undefined/null
          .map(u => 
            getUserId(u) === getUserId(selectedGoalUser) ? updatedUser : u
          )
        );
      }
      
      showSuccess(`Meta "${editGoalFormData.title}" actualizada correctamente`);
      setShowEditGoalModal(false);
      setSelectedGoal(null);
      setSelectedGoalUser(null);
    } catch (error: any) {
      showError(error.message || 'Error al actualizar meta');
    } finally {
      setLoading(false);
    }
  };

  // Función para cambiar estado de meta rápidamente
  const handleQuickStatusChange = async (goalId: number, newStatus: string, user: User, goalTitle: string) => {
    try {
      setLoading(true);
      const goalToUpdate = user.goals?.find(g => g.id === goalId);
      if (!goalToUpdate) return;

      const goalPayload = {
        ...goalToUpdate,
        status: newStatus as 'pending' | 'in_progress' | 'completed' | 'overdue',
        // Si se marca como completada, poner progreso al 100%
        progress: newStatus === 'completed' ? 100 : goalToUpdate.progress
      };

      const updatedUser = await UserService.updateGoal(getUserId(user), goalId, goalPayload);
      
      // Actualizar lista local - verificar que updatedUser no sea undefined
      if (updatedUser) {
        setUsers(prev => prev
          .filter(u => u !== undefined && u !== null) // 🛡️ Filtrar elementos undefined/null
          .map(u => 
            getUserId(u) === getUserId(user) ? updatedUser : u
          )
        );
      }
      
      const statusNames = {
        pending: 'Pendiente',
        in_progress: 'En Progreso', 
        completed: 'Completada',
        overdue: 'Vencida'
      };
      
      showSuccess(`Meta "${goalTitle}" marcada como: ${statusNames[newStatus as keyof typeof statusNames]}`);
    } catch (error: any) {
      showError(error.message || 'Error al cambiar estado de meta');
    } finally {
      setLoading(false);
    }
  };

  // Función para eliminar todas las metas de un usuario específico
  const handleDeleteAllUserGoals = async (user: User) => {
    const goalsCount = user.goals?.length || 0;
    if (goalsCount === 0) {
      showError('Este usuario no tiene metas para eliminar');
      return;
    }

    const confirmed = window.confirm(
      `⚠️ ELIMINAR TODAS LAS METAS\n\n` +
      `Usuario: ${user.firstName} ${user.lastName}\n` +
      `Total de metas: ${goalsCount}\n\n` +
      `Esta acción no se puede deshacer.\n` +
      `¿Estás seguro de continuar?`
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      showSuccess(`Eliminando ${goalsCount} metas de ${user.firstName} ${user.lastName}...`);
      
      // Eliminar todas las metas una por una
      const goalIds = user.goals?.map(g => g.id) || [];
      for (const goalId of goalIds) {
        await UserService.deleteGoal(getUserId(user), goalId);
      }

      // Actualizar usuario en la lista local
      const updatedUser = { ...user, goals: [] };
      setUsers(prev => prev
        .filter(u => u !== undefined && u !== null) // 🛡️ Filtrar elementos undefined/null
        .map(u => 
          getUserId(u) === getUserId(user) ? updatedUser : u
        )
      );
      
      showSuccess(`✅ Se eliminaron ${goalsCount} metas de ${user.firstName} ${user.lastName}`);
      
    } catch (error: any) {
      showError(error.message || 'Error al eliminar metas del usuario');
    } finally {
      setLoading(false);
    }
  };

  // Función para eliminación masiva de metas de múltiples usuarios
  const handleBulkDeleteAllGoals = async () => {
    if (usersSelectedForBulkDelete.length === 0) {
      showError('Selecciona al menos un usuario');
      return;
    }

    const selectedUsers = users.filter(u => usersSelectedForBulkDelete.includes(getUserId(u)));
    const totalGoals = selectedUsers.reduce((sum, user) => sum + (user.goals?.length || 0), 0);

    if (totalGoals === 0) {
      showError('Los usuarios seleccionados no tienen metas para eliminar');
      return;
    }

    const confirmed = window.confirm(
      `⚠️ ELIMINACIÓN MASIVA DE METAS\n\n` +
      `Usuarios seleccionados: ${selectedUsers.length}\n` +
      `Total de metas a eliminar: ${totalGoals}\n\n` +
      `Esta acción eliminará TODAS las metas de TODOS los usuarios seleccionados.\n` +
      `Esta acción no se puede deshacer.\n\n` +
      `¿Estás COMPLETAMENTE seguro de continuar?`
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      setBulkDeleteProgress({ total: selectedUsers.length, completed: 0, currentUser: '' });

      for (let i = 0; i < selectedUsers.length; i++) {
        const user = selectedUsers[i];
        const goalIds = user.goals?.map(g => g.id) || [];
        
        setBulkDeleteProgress(prev => prev ? {
          ...prev,
          currentUser: `${user.firstName} ${user.lastName}`,
          completed: i
        } : null);

        if (goalIds.length > 0) {
          // Eliminar todas las metas del usuario
          for (const goalId of goalIds) {
            await UserService.deleteGoal(getUserId(user), goalId);
          }

          // Actualizar usuario en la lista local
          setUsers(prev => prev
            .filter(u => u !== undefined && u !== null) // 🛡️ Filtrar elementos undefined/null
            .map(u => 
              getUserId(u) === getUserId(user) ? { ...u, goals: [] } : u
            )
          );
        }
      }

      setBulkDeleteProgress(prev => prev ? { ...prev, completed: selectedUsers.length } : null);
      
      showSuccess(`✅ Se eliminaron ${totalGoals} metas de ${selectedUsers.length} usuarios`);
      
      // Limpiar selección
      setUsersSelectedForBulkDelete([]);
      setShowBulkDeleteModal(false);
      
    } catch (error: any) {
      showError(error.message || 'Error durante la eliminación masiva');
    } finally {
      setLoading(false);
      setBulkDeleteProgress(null);
    }
  };

  // Funciones para manejo de selección múltiple
  const toggleUserForBulkDelete = (userId: string) => {
    setUsersSelectedForBulkDelete(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllUsersForBulkDelete = () => {
    setUsersSelectedForBulkDelete(
      users
        .filter(u => u !== undefined && u !== null) // 🛡️ Filtrar elementos undefined/null
        .map(u => getUserId(u))
    );
  };

  const clearBulkDeleteSelection = () => {
    setUsersSelectedForBulkDelete([]);
  };

  const handleMigrateCourseNames = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/migrate-course-names', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        showSuccess(`${result.message}. ${result.updatedCount} usuarios actualizados.`);
        await loadUsers(); // Recargar la lista de usuarios
      } else {
        showError(result.message || 'Error en la migración');
      }
    } catch (error) {
      console.error('Error en migración:', error);
      showError('Error al ejecutar la migración de cursos');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // FUNCIONES PARA ACTUALIZACIÓN ANUAL DE DOCENTES
  // ============================================

  // Paso 1: Generar backup de usuarios actuales (ahora se usa antes de ejecutar)
  const generateUsersBackup = async () => {
    try {
      showSuccess('Generando backup de usuarios actuales...');
      const allUsers = await UserService.getAllUsers();
      
      const backup = {
        exportDate: new Date().toISOString(),
        totalUsers: allUsers.length,
        users: allUsers.map(user => ({
          ...user,
          _backupNote: 'Backup generado antes de actualización anual'
        }))
      };

      setBackupData(backup);

      // Descargar como JSON
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `backup-usuarios-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // También descargar como Excel
      downloadUsersAsExcel(allUsers, `backup-usuarios-${new Date().toISOString().split('T')[0]}.xlsx`);

      showSuccess(`✅ Backup generado: ${allUsers.length} usuarios guardados`);
      return true;
    } catch (error: any) {
      showError('Error al generar backup: ' + error.message);
      console.error('Error generating backup:', error);
      return false;
    }
  };

  // Función auxiliar para descargar usuarios como Excel
  const downloadUsersAsExcel = (users: User[], filename: string) => {
    const headers = [
      'Username',
      'Email',
      'FirstName',
      'LastName',
      'Role',
      'AllowedCourses',
      'IsActive',
      'LastLogin',
      'CreatedAt'
    ];

    const rows = users.map(user => [
      user.username,
      user.email,
      user.firstName,
      user.lastName,
      user.role,
      user.allowedCourses.join(';'),
      user.isActive ? 'Sí' : 'No',
      user.lastLogin || '',
      user.createdAt
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Paso 3: Procesar archivo Excel de nuevos usuarios  
  const handleNewUsersFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const XLSX = (window as any).XLSX;
        
        if (!XLSX) {
          showError('Librería XLSX no disponible. Por favor, recarga la página.');
          return;
        }

        // Procesar Excel usando xlsx
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
        
        // Obtener headers y normalizar
        const headers = (jsonData[0] as string[]).map((h: string) => h?.toString().toLowerCase().trim());
        
        // Mapeo de headers en español a índices
        const headerMap: {[key: string]: number} = {};
        headers.forEach((header, index) => {
          const normalized = header.toLowerCase();
          if (normalized.includes('usuario') || normalized === 'username') headerMap['username'] = index;
          else if (normalized.includes('correo') || normalized.includes('email')) headerMap['email'] = index;
          else if (normalized === 'nombre' || normalized === 'firstname') headerMap['firstName'] = index;
          else if (normalized === 'apellido' || normalized === 'lastname') headerMap['lastName'] = index;
          else if (normalized === 'rol' || normalized === 'role') headerMap['role'] = index;
          else if (normalized.includes('curso') || normalized.includes('allowedcourses')) headerMap['courses'] = index;
          else if (normalized.includes('dimension') || normalized.includes('alloweddimensions')) headerMap['dimensions'] = index;
          else if (normalized.includes('contraseña') || normalized.includes('password')) headerMap['password'] = index;
        });
        
        // Saltar la primera fila (headers)
        const usersFromFile = (jsonData as any[]).slice(1)
          .filter((row: any[]) => row && row.length > 0 && row[headerMap['username']])
          .map((row: any[]) => {
            // Convertir rol de español a inglés
            const rolValue = row[headerMap['role']]?.toString().toLowerCase().trim() || 'profesor';
            let role: 'admin' | 'teacher' = 'teacher';
            if (rolValue.includes('admin') || rolValue.includes('administrador')) {
              role = 'admin';
            }

            // Procesar dimensiones - convertir nombres a IDs
            let allowedDimensions: number[] = [];
            if (headerMap['dimensions'] !== undefined && row[headerMap['dimensions']]) {
              const dimensionsStr = row[headerMap['dimensions']].toString().trim();
              
              if (dimensionsStr) {
                // Crear mapeo de nombre a ID (case insensitive)
                const dimensionNameToId: {[key: string]: number} = {};
                allDimensionsList.forEach(dim => {
                  dimensionNameToId[dim.name.toLowerCase()] = dim.id;
                });

                // Separar por punto y coma
                const dimensionParts = dimensionsStr.split(';').map(d => d.trim()).filter(d => d);
                
                dimensionParts.forEach(part => {
                  // Intentar convertir a número primero (por si viene como ID)
                  const asNumber = parseInt(part);
                  if (!isNaN(asNumber) && asNumber > 0) {
                    allowedDimensions.push(asNumber);
                  } else {
                    // Buscar por nombre (case insensitive)
                    const dimensionId = dimensionNameToId[part.toLowerCase()];
                    if (dimensionId) {
                      allowedDimensions.push(dimensionId);
                    }
                  }
                });

                // Eliminar duplicados
                allowedDimensions = [...new Set(allowedDimensions)];
              }
            }

            return {
              username: row[headerMap['username']]?.toString() || '',
              email: row[headerMap['email']]?.toString() || '',
              firstName: row[headerMap['firstName']]?.toString() || '',
              lastName: row[headerMap['lastName']]?.toString() || '',
              role: role,
              allowedCourses: row[headerMap['courses']] 
                ? row[headerMap['courses']].toString().split(';').map((c: string) => c.trim()).filter((c: string) => c)
                : [],
              allowedDimensions: allowedDimensions,
              password: row[headerMap['password']]?.toString() || '',
              isActive: true,
              mustChangePassword: true
            };
          });

        setNewUsersData(usersFromFile);
        showSuccess(`✅ ${usersFromFile.length} nuevos usuarios cargados para revisión`);
        analyzeUsersChanges(usersFromFile);
      } catch (error: any) {
        showError('Error al procesar archivo: ' + error.message);
        console.error('Error processing file:', error);
      }
    };

    // Leer como binario para Excel
    reader.readAsBinaryString(file);
  };

  // NUEVA FUNCIÓN: Analizar cambios entre usuarios actuales y nuevos
  const analyzeUsersChanges = (newUsers: any[]) => {
    try {
      // Filtrar usuarios actuales según configuración
      let currentUsers = users;
      if (annualUpdateConfig.preserveAdmins) {
        currentUsers = users.filter(user => user.role !== 'admin');
      }

      const toCreate: any[] = [];
      const toUpdate: {user: User, newData: any, changes: string[]}[] = [];
      const toMaintain: User[] = [];
      const toDeactivate: User[] = [];

      // Analizar cada usuario del archivo
      newUsers.forEach(newUser => {
        // Buscar por email (más confiable)
        const existingUser = users.find(u => 
          u.email.toLowerCase() === newUser.email.toLowerCase()
        );

        if (existingUser) {
          // Usuario existe - verificar si hay cambios
          const changes: string[] = [];

          if (existingUser.firstName !== newUser.firstName) {
            changes.push(`Nombre: "${existingUser.firstName}" → "${newUser.firstName}"`);
          }
          if (existingUser.lastName !== newUser.lastName) {
            changes.push(`Apellido: "${existingUser.lastName}" → "${newUser.lastName}"`);
          }
          if (existingUser.username !== newUser.username) {
            changes.push(`Usuario: "${existingUser.username}" → "${newUser.username}"`);
          }
          if (existingUser.email !== newUser.email) {
            changes.push(`Email: "${existingUser.email}" → "${newUser.email}"`);
          }
          if (existingUser.role !== newUser.role) {
            const oldRole = existingUser.role === 'admin' ? 'Administrador' : 'Profesor';
            const newRole = newUser.role === 'admin' ? 'Administrador' : 'Profesor';
            changes.push(`Rol: "${oldRole}" → "${newRole}"`);
          }

          // Comparar cursos con detalle
          const oldCourses = [...existingUser.allowedCourses].sort();
          const newCourses = [...newUser.allowedCourses].sort();
          
          if (JSON.stringify(oldCourses) !== JSON.stringify(newCourses)) {
            // Detectar cursos agregados
            const addedCourses = newCourses.filter(c => !oldCourses.includes(c));
            // Detectar cursos eliminados
            const removedCourses = oldCourses.filter(c => !newCourses.includes(c));
            
            if (addedCourses.length > 0 && removedCourses.length > 0) {
              // Hay cursos agregados y eliminados
              changes.push(`Cursos eliminados: ${removedCourses.join(', ')}`);
              changes.push(`Cursos agregados: ${addedCourses.join(', ')}`);
            } else if (addedCourses.length > 0) {
              // Solo cursos agregados
              changes.push(`Cursos agregados: ${addedCourses.join(', ')}`);
            } else if (removedCourses.length > 0) {
              // Solo cursos eliminados
              changes.push(`Cursos eliminados: ${removedCourses.join(', ')}`);
            } else {
              // Los cursos son diferentes pero no podemos determinar qué cambió
              changes.push(`Cursos: [${oldCourses.join(', ')}] → [${newCourses.join(', ')}]`);
            }
          }

          // Comparar dimensiones con detalle - asegurar que sean números
          const oldDimensions = [...(existingUser.allowedDimensions || [])].map(d => Number(d)).filter(d => !isNaN(d)).sort((a, b) => a - b);
          const newDimensions = [...(newUser.allowedDimensions || [])].map(d => Number(d)).filter(d => !isNaN(d)).sort((a, b) => a - b);
          
          if (JSON.stringify(oldDimensions) !== JSON.stringify(newDimensions)) {
            // Crear mapeo de ID a nombre
            const dimensionMap: {[key: number]: string} = {};
            allDimensionsList.forEach(dim => {
              dimensionMap[dim.id] = dim.name;
            });

            // Detectar dimensiones agregadas (comparación numérica)
            const addedDimensions = newDimensions.filter(d => !oldDimensions.some(od => od === d));
            // Detectar dimensiones eliminadas (comparación numérica)
            const removedDimensions = oldDimensions.filter(d => !newDimensions.some(nd => nd === d));
            
            // Convertir IDs a nombres para mostrar
            const addedNames = addedDimensions.map(id => dimensionMap[id] || `ID ${id}`);
            const removedNames = removedDimensions.map(id => dimensionMap[id] || `ID ${id}`);
            
            if (addedDimensions.length > 0 && removedDimensions.length > 0) {
              // Hay dimensiones agregadas y eliminadas
              changes.push(`Dimensiones eliminadas: ${removedNames.join(', ')}`);
              changes.push(`Dimensiones agregadas: ${addedNames.join(', ')}`);
            } else if (addedDimensions.length > 0) {
              // Solo dimensiones agregadas
              changes.push(`Dimensiones agregadas: ${addedNames.join(', ')}`);
            } else if (removedDimensions.length > 0) {
              // Solo dimensiones eliminadas
              changes.push(`Dimensiones eliminadas: ${removedNames.join(', ')}`);
            } else {
              // Las dimensiones son diferentes
              const oldNames = oldDimensions.map(id => dimensionMap[id] || `ID ${id}`);
              const newNames = newDimensions.map(id => dimensionMap[id] || `ID ${id}`);
              const oldDimStr = oldNames.length > 0 ? oldNames.join(', ') : '(ninguna)';
              const newDimStr = newNames.length > 0 ? newNames.join(', ') : '(ninguna)';
              changes.push(`Dimensiones: [${oldDimStr}] → [${newDimStr}]`);
            }
          }

          if (changes.length > 0) {
            toUpdate.push({
              user: existingUser,
              newData: newUser,
              changes
            });
          } else {
            toMaintain.push(existingUser);
          }
        } else {
          // Usuario no existe - crear
          toCreate.push(newUser);
        }
      });

      // Identificar usuarios a desactivar/eliminar (existen en BD pero no en archivo)
      currentUsers.forEach(existingUser => {
        const inNewFile = newUsers.some(newUser =>
          existingUser.email.toLowerCase() === newUser.email.toLowerCase()
        );
        
        if (!inNewFile) {
          toDeactivate.push(existingUser);
        }
      });

      setUsersToCreate(toCreate);
      setUsersToUpdate(toUpdate);
      setUsersToMaintain(toMaintain);
      setUsersToDeactivate(toDeactivate);

      showSuccess(`Análisis completado: ${toCreate.length} nuevos, ${toUpdate.length} con cambios, ${toMaintain.length} sin cambios, ${toDeactivate.length} ${annualUpdateConfig.deleteOldUsers ? 'a eliminar' : 'a desactivar'}`);
      setAnnualUpdateStep('review');
    } catch (error: any) {
      showError('Error al analizar cambios: ' + error.message);
    }
  };

  // NUEVA FUNCIÓN: Descargar plantilla Excel con usuarios actuales
  const downloadCurrentUsersTemplate = () => {
    try {
      const XLSX = (window as any).XLSX;
      if (!XLSX) {
        showError('Librería XLSX no disponible. Por favor, recarga la página.');
        return;
      }

      // Crear mapeo de ID a nombre de dimensión
      const dimensionMap: {[key: number]: string} = {};
      allDimensionsList.forEach(dim => {
        dimensionMap[dim.id] = dim.name;
      });

      // Preparar datos para Excel - NOMBRES EN ESPAÑOL
      const headers = [
        'Usuario',
        'Correo Electrónico',
        'Nombre',
        'Apellido',
        'Rol',
        'Cursos Asignados',
        'Dimensiones Observables',
        'Contraseña'
      ];

      const usersToExport = annualUpdateConfig.preserveAdmins 
        ? users.filter(u => u.role !== 'admin')
        : users;

      const rows = usersToExport.map(user => {
        // Convertir IDs de dimensiones a nombres (asegurar que sean números)
        const dimensionNames = (user.allowedDimensions || [])
          .map((id: any) => {
            const numId = Number(id);
            return dimensionMap[numId];
          })
          .filter((name: string | undefined) => name) // Filtrar nombres undefined
          .join(';');

        return [
          user.username,
          user.email,
          user.firstName || '',
          user.lastName || '',
          user.role === 'admin' ? 'Administrador' : 'Profesor',
          (user.allowedCourses || []).join(';'),
          dimensionNames, // Nombres de dimensiones en lugar de IDs
          '' // Password vacío para que se genere automáticamente
        ];
      });

      // Crear workbook y worksheet
      const wb = XLSX.utils.book_new();
      const wsData = [headers, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Ajustar ancho de columnas
      ws['!cols'] = [
        { wch: 20 }, // Usuario
        { wch: 30 }, // Correo Electrónico
        { wch: 18 }, // Nombre
        { wch: 18 }, // Apellido
        { wch: 15 }, // Rol
        { wch: 45 }, // Cursos Asignados
        { wch: 50 }, // Dimensiones Observables (más ancho para nombres)
        { wch: 15 }  // Contraseña
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');

      // Descargar archivo
      const fileName = `plantilla-usuarios-actuales-${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      showSuccess(`✅ Plantilla descargada: ${usersToExport.length} usuarios`);
    } catch (error: any) {
      showError('Error al generar plantilla: ' + error.message);
      console.error('Error generating template:', error);
    }
  };

  // Función para descargar credenciales de usuarios nuevos creados
  const downloadNewUsersCredentials = (credentials: {
    firstName: string;
    lastName: string;
    email: string;
    username: string;
    password: string;
  }[]) => {
    try {
      const XLSX = (window as any).XLSX;
      if (!XLSX) {
        showError('Librería XLSX no disponible. Por favor, recarga la página.');
        return;
      }

      // Preparar datos para Excel
      const headers = [
        'Nombre',
        'Apellido',
        'Correo Electrónico',
        'Usuario',
        'Contraseña Temporal',
        'Nota'
      ];

      const rows = credentials.map(cred => [
        cred.firstName,
        cred.lastName,
        cred.email,
        cred.username,
        cred.password,
        'El usuario debe cambiar su contraseña en el primer inicio de sesión'
      ]);

      // Crear workbook y worksheet
      const wb = XLSX.utils.book_new();
      const wsData = [headers, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Ajustar ancho de columnas
      ws['!cols'] = [
        { wch: 18 }, // Nombre
        { wch: 18 }, // Apellido
        { wch: 35 }, // Correo Electrónico
        { wch: 20 }, // Usuario
        { wch: 20 }, // Contraseña Temporal
        { wch: 55 }  // Nota
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Credenciales Nuevos Usuarios');

      // Descargar archivo
      const fileName = `credenciales-usuarios-nuevos-${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      showSuccess(`📋 Archivo de credenciales descargado: ${credentials.length} usuarios nuevos`);
    } catch (error: any) {
      showError('Error al generar archivo de credenciales: ' + error.message);
      console.error('Error generating credentials file:', error);
    }
  };

  // Paso 4: Ejecutar actualización completa (con backup automático)
  const executeAnnualUpdate = async () => {
    try {
      setAnnualUpdateStep('processing');
      
      // FASE 0: Generar backup automáticamente
      setAnnualUpdateProgress({
        phase: 'Generando backup de seguridad',
        current: 0,
        total: usersToDeactivate.length + usersToUpdate.length + usersToCreate.length,
        currentItem: 'Backup en progreso...',
        errors: []
      });

      const backupSuccess = await generateUsersBackup();
      if (!backupSuccess) {
        throw new Error('No se pudo generar el backup. Actualización cancelada.');
      }

      const errors: string[] = [];
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No hay sesión activa');
      }

      let currentProgress = 0;

      // FASE 1: Desactivar o eliminar usuarios antiguos
      setAnnualUpdateProgress(prev => ({
        ...prev!,
        phase: 'Procesando usuarios antiguos',
        currentItem: ''
      }));

      for (let i = 0; i < usersToDeactivate.length; i++) {
        const user = usersToDeactivate[i];
        currentProgress++;
        setAnnualUpdateProgress(prev => ({
          ...prev!,
          current: currentProgress,
          currentItem: `${user.firstName} ${user.lastName}`
        }));

        try {
          if (annualUpdateConfig.deleteOldUsers) {
            await fetch(`${API_BASE_URL}/api/users/${getUserId(user)}`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
          } else {
            await fetch(`${API_BASE_URL}/api/users/${getUserId(user)}`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ isActive: false })
            });
          }
        } catch (error: any) {
          errors.push(`Error con ${user.username}: ${error.message}`);
        }

        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // FASE 2: Actualizar usuarios con cambios
      setAnnualUpdateProgress(prev => ({
        ...prev!,
        phase: 'Actualizando usuarios existentes',
        currentItem: ''
      }));

      for (let i = 0; i < usersToUpdate.length; i++) {
        const {user, newData} = usersToUpdate[i];
        currentProgress++;
        setAnnualUpdateProgress(prev => ({
          ...prev!,
          current: currentProgress,
          currentItem: `${user.firstName} ${user.lastName}`
        }));

        try {
          // Asegurar que las dimensiones sean números
          const allowedDimensions = (newData.allowedDimensions || [])
            .map((d: any) => Number(d))
            .filter((d: number) => !isNaN(d));
          
          await fetch(`${API_BASE_URL}/api/users/${getUserId(user)}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              username: newData.username,
              email: newData.email,
              firstName: newData.firstName,
              lastName: newData.lastName,
              role: newData.role,
              allowedCourses: newData.allowedCourses,
              allowedDimensions: allowedDimensions,
              isActive: true
            })
          });
        } catch (error: any) {
          errors.push(`Error actualizando ${user.username}: ${error.message}`);
        }

        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // FASE 3: Crear nuevos usuarios
      setAnnualUpdateProgress(prev => ({
        ...prev!,
        phase: 'Creando nuevos usuarios',
        currentItem: ''
      }));

      // Array para guardar credenciales de usuarios creados exitosamente
      const createdUsersCredentials: {
        firstName: string;
        lastName: string;
        email: string;
        username: string;
        password: string;
      }[] = [];

      for (let i = 0; i < usersToCreate.length; i++) {
        const userData = usersToCreate[i];
        currentProgress++;
        setAnnualUpdateProgress(prev => ({
          ...prev!,
          current: currentProgress,
          currentItem: `${userData.firstName} ${userData.lastName}`
        }));

        try {
          // Generar password temporal si no hay uno
          const password = userData.password && userData.password.trim() !== '' 
            ? userData.password 
            : `Temp${Math.random().toString(36).slice(-6)}!`;
          
          // Asegurar que las dimensiones sean números
          const allowedDimensions = (userData.allowedDimensions || [])
            .map((d: any) => Number(d))
            .filter((d: number) => !isNaN(d));
          
          const response = await fetch(`${API_BASE_URL}/api/users`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              ...userData,
              password,
              allowedDimensions,
              mustChangePassword: true
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Error al crear usuario');
          }
          
          // Guardar credenciales del usuario creado exitosamente
          createdUsersCredentials.push({
            firstName: userData.firstName,
            lastName: userData.lastName,
            email: userData.email,
            username: userData.username,
            password: password
          });
        } catch (error: any) {
          errors.push(`Error con ${userData.username}: ${error.message}`);
        }

        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setAnnualUpdateProgress(prev => ({
        ...prev!,
        phase: 'Completado',
        errors
      }));

      await loadUsers();
      setAnnualUpdateStep('complete');
      
      // Descargar archivo con credenciales de usuarios nuevos si hay alguno
      if (createdUsersCredentials.length > 0) {
        downloadNewUsersCredentials(createdUsersCredentials);
      }
      
      if (errors.length === 0) {
        showSuccess('✅ Actualización anual completada exitosamente');
      } else {
        showError(`Actualización completada con ${errors.length} errores. Revisa el log.`);
      }
    } catch (error: any) {
      showError('Error en actualización anual: ' + error.message);
      console.error('Error in annual update:', error);
    }
  };

  const resetAnnualUpdate = () => {
    setAnnualUpdateStep('init');
    setBackupData(null);
    setNewUsersData([]);
    setUsersToDeactivate([]);
    setUsersToMaintain([]);
    setUsersToCreate([]);
    setUsersToUpdate([]);
    setAnnualUpdateProgress(null);
  };

  // ============================================
  // FUNCIONES PARA ACTUALIZACIÓN ANUAL DE ESTUDIANTES
  // ============================================
  
  const resetStudentAnnualUpdate = () => {
    setStudentAnnualUpdateStep('init');
    setStudentNewData([]);
    setStudentsToUpdate([]);
    setStudentsToCreate([]);
    setStudentsToWithdraw([]);
    setStudentAnnualUpdateProgress(null);
  };

  // Helper function to get next course
  const getNextCourse = (currentCourse: string): string => {
    const COURSE_PROGRESSION: {[key: string]: string} = {
      'Medio Mayor': 'Pre Kinder',
      'Pre Kinder': 'Kinder',
      'Kinder': 'Primero básico',
      'Primero básico': 'Segundo básico',
      'Segundo básico': 'Tercero básico',
      'Tercero básico': 'Cuarto básico',
      'Cuarto básico': 'Quinto básico',
      'Quinto básico': 'Sexto básico',
      'Sexto básico': 'Séptimo básico',
      'Séptimo básico': 'Octavo básico',
      'Octavo básico': 'Primero medio',
      'Primero medio': 'Segundo medio',
      'Segundo medio': 'Tercero medio',
      'Tercero medio': 'Cuarto medio',
      'Cuarto medio': 'Egresado'
    };
    
    // Extract base course name (without section letter)
    const match = currentCourse.match(/^(.+?)(\s+[A-F])?$/i);
    const baseCourse = match ? match[1].trim() : currentCourse;
    const section = match && match[2] ? match[2].trim() : '';
    
    // Normalize the base course name
    let normalizedBase = baseCourse;
    const normalizations: {[key: string]: string} = {
      'nivel medio mayor': 'Medio Mayor',
      'medio mayor': 'Medio Mayor',
      'prekinder': 'Pre Kinder',
      'pre kinder': 'Pre Kinder',
      'prekínder': 'Pre Kinder',
      'kinder': 'Kinder',
      'kínder': 'Kinder',
      'primero básico': 'Primero básico',
      'primero basico': 'Primero básico',
      'segundo básico': 'Segundo básico',
      'segundo basico': 'Segundo básico',
      'tercero básico': 'Tercero básico',
      'tercero basico': 'Tercero básico',
      'cuarto básico': 'Cuarto básico',
      'cuarto basico': 'Cuarto básico',
      'quinto básico': 'Quinto básico',
      'quinto basico': 'Quinto básico',
      'sexto básico': 'Sexto básico',
      'sexto basico': 'Sexto básico',
      'séptimo básico': 'Séptimo básico',
      'septimo básico': 'Séptimo básico',
      'septimo basico': 'Séptimo básico',
      'octavo básico': 'Octavo básico',
      'octavo basico': 'Octavo básico',
      'primero medio': 'Primero medio',
      'segundo medio': 'Segundo medio',
      'tercero medio': 'Tercero medio',
      'cuarto medio': 'Cuarto medio'
    };
    
    const lowerBase = baseCourse.toLowerCase();
    if (normalizations[lowerBase]) {
      normalizedBase = normalizations[lowerBase];
    }
    
    const nextBase = COURSE_PROGRESSION[normalizedBase] || 'Egresado';
    return section && nextBase !== 'Egresado' ? `${nextBase} ${section}` : nextBase;
  };

  // Parse student name into components
  const parseStudentName = (fullName: string): {firstName: string, paternalLastName: string, maternalLastName: string} => {
    const parts = (fullName || '').trim().split(/\s+/);
    if (parts.length === 0) return { firstName: '', paternalLastName: '', maternalLastName: '' };
    if (parts.length === 1) return { firstName: parts[0], paternalLastName: '', maternalLastName: '' };
    if (parts.length === 2) return { firstName: parts[0], paternalLastName: parts[1], maternalLastName: '' };
    
    // For Chilean format: APELLIDO_PATERNO APELLIDO_MATERNO NOMBRE(S)
    const allUppercase = parts.every(part => part === part.toUpperCase());
    if (allUppercase && parts.length >= 3) {
      return {
        firstName: parts.slice(2).join(' '),
        paternalLastName: parts[0],
        maternalLastName: parts[1]
      };
    }
    
    if (parts.length === 3) {
      return { firstName: parts[0], paternalLastName: parts[1], maternalLastName: parts[2] };
    }
    
    const lastNameStart = Math.max(parts.length - 2, 1);
    return {
      firstName: parts.slice(0, lastNameStart).join(' '),
      paternalLastName: parts[lastNameStart] || '',
      maternalLastName: parts[lastNameStart + 1] || ''
    };
  };

  // Download current students template
  const downloadStudentsTemplate = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/api/students-annual/export-template`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al obtener datos de estudiantes');
      }
      
      const data = await response.json();
      
      if (!data.success || !data.students) {
        throw new Error('Formato de respuesta inválido');
      }
      
      const XLSX = (window as any).XLSX;
      if (!XLSX) {
        showError('Librería XLSX no disponible. Por favor, recarga la página.');
        return;
      }
      
      // Prepare Excel data
      const headers = [
        'RUT/ID',
        'Nombre',
        'Apellido Paterno',
        'Apellido Materno',
        'Sexo',
        'Fecha Nacimiento',
        'Curso Actual',
        'Curso Año Siguiente'
      ];
      
      const rows = data.students.map((s: any) => [
        s.rut || '',
        s.firstName || '',
        s.paternalLastName || '',
        s.maternalLastName || '',
        s.gender || '',
        s.birthDate || '',
        s.currentCourse || '',
        s.nextCourse || ''
      ]);
      
      const wb = XLSX.utils.book_new();
      const wsData = [headers, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      
      // Column widths
      ws['!cols'] = [
        { wch: 15 }, // RUT
        { wch: 20 }, // Nombre
        { wch: 20 }, // Ap. Paterno
        { wch: 20 }, // Ap. Materno
        { wch: 8 },  // Sexo
        { wch: 15 }, // Fecha Nac.
        { wch: 20 }, // Curso Actual
        { wch: 20 }  // Curso Siguiente
      ];
      
      XLSX.utils.book_append_sheet(wb, ws, 'Estudiantes');
      XLSX.writeFile(wb, `plantilla-estudiantes-${new Date().getFullYear()}.xlsx`);
      
      showSuccess(`✅ Plantilla descargada: ${data.students.length} estudiantes`);
      setStudentAnnualUpdateStep('upload');
    } catch (error: any) {
      showError('Error al descargar plantilla: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle student file upload
  const handleStudentFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const XLSX = (window as any).XLSX;
        if (!XLSX) {
          showError('Librería XLSX no disponible');
          return;
        }
        
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        
        if (jsonData.length < 2) {
          showError('El archivo está vacío o no tiene el formato correcto');
          return;
        }
        
        // Parse rows (skip header)
        const students: any[] = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i] as any[];
          if (!row || row.length < 7 || !row[0]) continue;
          
          students.push({
            rut: String(row[0] || '').replace(/[^0-9kK]/gi, '').toUpperCase(),
            firstName: String(row[1] || '').trim(),
            paternalLastName: String(row[2] || '').trim(),
            maternalLastName: String(row[3] || '').trim(),
            gender: String(row[4] || '').toUpperCase(),
            birthDate: String(row[5] || ''),
            course: String(row[7] || row[6] || '').trim(), // nextCourse or currentCourse
            nextCourse: String(row[7] || '').trim()
          });
        }
        
        if (students.length === 0) {
          showError('No se encontraron estudiantes válidos en el archivo');
          return;
        }
        
        setStudentNewData(students);
        analyzeStudentChanges(students);
      } catch (error: any) {
        showError('Error al procesar archivo: ' + error.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Analyze student changes
  const analyzeStudentChanges = async (newStudents: any[]) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/api/students-annual/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ students: newStudents })
      });
      
      if (!response.ok) {
        throw new Error('Error al analizar cambios');
      }
      
      const data = await response.json();
      
      if (!data.success || !data.preview) {
        throw new Error('Formato de respuesta inválido');
      }
      
      setStudentsToUpdate(data.preview.studentsToUpdate || []);
      setStudentsToCreate(data.preview.studentsToCreate || []);
      setStudentsToWithdraw(data.preview.studentsToWithdraw || []);
      setStudentAnnualUpdateStep('review');
      
      showSuccess(`Análisis completado: ${data.preview.summary.toUpdate} a actualizar, ${data.preview.summary.toCreate} nuevos, ${data.preview.summary.toWithdraw} a retirar`);
    } catch (error: any) {
      showError('Error al analizar cambios: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Execute student annual update
  const executeStudentAnnualUpdate = async () => {
    try {
      setStudentAnnualUpdateStep('processing');
      setStudentAnnualUpdateProgress({
        phase: 'Iniciando actualización...',
        current: 0,
        total: studentsToUpdate.length + studentsToCreate.length + studentsToWithdraw.length,
        currentItem: '',
        errors: []
      });
      
      const token = localStorage.getItem('token');
      const currentYear = selectedAcademicYear;
      
      const response = await fetch(`${API_BASE_URL}/api/students-annual/execute`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          studentsToUpdate,
          studentsToCreate,
          studentsToWithdraw,
          academicYear: currentYear
        })
      });
      
      if (!response.ok) {
        throw new Error('Error al ejecutar actualización');
      }
      
      const data = await response.json();
      
      setStudentAnnualUpdateProgress({
        phase: 'Completado',
        current: data.results.updated + data.results.created + data.results.withdrawn,
        total: data.results.updated + data.results.created + data.results.withdrawn,
        currentItem: '',
        errors: data.results.errors || []
      });
      
      setStudentAnnualUpdateStep('complete');
      
      if (data.results.errors && data.results.errors.length > 0) {
        showError(`Actualización completada con ${data.results.errors.length} errores`);
      } else {
        showSuccess('✅ Actualización anual de estudiantes completada exitosamente');
      }
    } catch (error: any) {
      showError('Error en actualización: ' + error.message);
      setStudentAnnualUpdateStep('confirm');
    }
  };

  // Render Backup View
  const renderBackupView = () => {
    return (
      <BackupManager
        students={propStudents}
        dimensions={propDimensions}
        username={propUsername}
        onBackupComplete={onBackupComplete}
      />
    );
  };

  // Render Dimensions View
  // Eliminado: vista de actualización de dimensiones en frontend

  // Apply Skill Levels Configuration
  const applySkillLevelsConfiguration = async () => {
    setIsApplyingSkillLevels(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token de autenticación no encontrado');
      }

      const response = await fetch('/api/admin/configure-skill-levels', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          levelConfiguration: skillLevelsConfig
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al configurar skill levels');
      }

      setSuccess(`✅ ${data.message}`);
      console.log('📊 Configuración aplicada:', data);
      
    } catch (error: any) {
      console.error('❌ Error aplicando configuración:', error);
      setError(error.message || 'Error al aplicar configuración');
    } finally {
      setIsApplyingSkillLevels(false);
    }
  };

  // Render Skill Levels Configuration View
  const renderSkillLevelsView = () => {
    const levelLabels = {
      0: 'No Iniciado',
      1: 'Iniciando', 
      2: 'En Desarrollo',
      3: 'Desarrollando',
      4: 'Parcialmente Logrado',
      5: 'En Progreso',
      6: 'Logrado',
      7: 'Bien Logrado',
      8: 'Excelentemente Logrado'
    };

    const groupedCourses = {
      'Educación Parvularia': ['Medio Mayor A', 'Medio Mayor B', 'Medio Mayor C', 'Medio Mayor D', 'Pre Kinder A', 'Pre Kinder B', 'Pre Kinder C', 'Kinder A', 'Kinder B', 'Kinder C'],
      'Educación Básica': ['Primero básico A', 'Primero básico B', 'Primero básico C', 'Segundo básico A', 'Segundo básico B', 'Segundo básico C', 'Tercero básico A', 'Tercero básico B', 'Tercero básico C'],
      'Educación Básica Superior': ['Cuarto básico A', 'Cuarto básico B', 'Cuarto básico C', 'Quinto básico A', 'Quinto básico B', 'Quinto básico C', 'Sexto básico A', 'Sexto básico B', 'Sexto básico C']
    };

    return (
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="border-b border-gray-200 pb-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <FaSlidersH className="mr-3 text-indigo-600" />
            Configurar Skill Levels por Curso
          </h2>
          <p className="mt-2 text-gray-600">
            Configure el nivel de habilidades que se aplicará masivamente a todos los estudiantes de cada curso.
            Esto representa el nivel del año anterior como base para el progreso actual.
          </p>
        </div>

        {/* Warning */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <FaExclamationTriangle className="text-yellow-500 mt-1 mr-3" />
            <div>
              <h3 className="text-yellow-800 font-semibold">⚠️ Configuración Masiva</h3>
              <p className="text-yellow-700 text-sm mt-1">
                Esta acción aplicará los niveles seleccionados a <strong>TODAS las 514 habilidades</strong> de 
                <strong> TODOS los estudiantes</strong> del curso correspondiente. Esta operación NO se puede deshacer.
              </p>
            </div>
          </div>
        </div>

        {/* Configuration Grid */}
        <div className="space-y-8">
          {Object.entries(groupedCourses).map(([groupName, courses]) => (
            <div key={groupName} className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FaGraduationCap className="mr-2 text-indigo-500" />
                {groupName}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {courses.map(course => (
                  <div key={course} className="bg-gray-50 p-4 rounded-lg border">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {course}
                    </label>
                    <select
                      value={skillLevelsConfig[course] !== undefined ? skillLevelsConfig[course] : 4}
                      onChange={(e) => setSkillLevelsConfig(prev => ({
                        ...prev,
                        [course]: parseInt(e.target.value)
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      {Object.entries(levelLabels).map(([value, label]) => (
                        <option key={value} value={value}>
                          {value} - {label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Apply Button */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Aplicar Configuración</h3>
              <p className="text-gray-600 text-sm mt-1">
                Se configurarán las 514 habilidades para todos los estudiantes según los niveles seleccionados.
              </p>
            </div>
            <button
              onClick={applySkillLevelsConfiguration}
              disabled={isApplyingSkillLevels}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center space-x-2 ${
                isApplyingSkillLevels
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
              }`}
            >
              {isApplyingSkillLevels ? (
                <>
                  <FaSpinner className="animate-spin" />
                  <span>Aplicando...</span>
                </>
              ) : (
                <>
                  <FaCheck />
                  <span>Aplicar Configuración</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <FaCheck className="text-green-500 mt-1 mr-3" />
              <div>
                <h3 className="text-green-800 font-semibold">Configuración Aplicada</h3>
                <p className="text-green-700 text-sm mt-1">{success}</p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <FaExclamationTriangle className="text-red-500 mt-1 mr-3" />
              <div>
                <h3 className="text-red-800 font-semibold">Error</h3>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderNavigationTabs = () => {
    const currentUser = UserService.getCurrentUser();
    const isTeacher = currentUser?.role === 'teacher';
    
    // Filtrar tabs según el rol del usuario
    const allTabs = [
      { id: 'users', label: 'Usuarios', icon: FaUsers },
      { id: 'estudiantes', label: 'Estudiantes', icon: FaGraduationCap },
      { id: 'informes-retiros', label: 'Informes y Retiros', icon: FaFileAlt },
      { id: 'teachers-reports', label: 'Docentes Informes', icon: FaChalkboardTeacher },
      { id: 'annual-update', label: 'Actualización Anual', icon: FaSync }
    ];

    const tabs = isTeacher 
      ? allTabs.filter(tab => ['users'].includes(tab.id)) // Solo usuarios para profesores
      : allTabs; // Todas para administradores

    return (
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex w-full">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = currentView === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setCurrentView(tab.id as any)}
                className={`flex-1 py-3 px-2 border-b-2 font-medium text-sm flex items-center justify-center space-x-2 transition-colors ${
                  isActive
                    ? 'border-indigo-500 text-indigo-600 bg-indigo-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <Icon className={`text-lg ${isActive ? 'text-indigo-600' : 'text-gray-400'}`} />
                <span className="whitespace-nowrap">{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    );
  };

  const renderOverviewDashboard = () => {
    const stats = getUserStats();
    
    return (
      <div className="p-6 space-y-6">
        {/* Quick Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-blue-100 text-sm font-medium">Total Usuarios</div>
                <div className="text-3xl font-bold">{stats.totalUsers}</div>
              </div>
              <FaUsers className="text-4xl text-blue-200" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-green-100 text-sm font-medium">Activos</div>
                <div className="text-3xl font-bold">{stats.activeUsers}</div>
              </div>
              <FaEye className="text-4xl text-green-200" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-purple-100 text-sm font-medium">Administradores</div>
                <div className="text-3xl font-bold">{stats.adminUsers}</div>
              </div>
              <FaCrown className="text-4xl text-purple-200" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-yellow-100 text-sm font-medium">Profesores</div>
                <div className="text-3xl font-bold">{stats.teacherUsers}</div>
              </div>
              <FaUserTie className="text-4xl text-yellow-200" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-red-100 text-sm font-medium">Bloqueados</div>
                <div className="text-3xl font-bold">{stats.lockedUsers}</div>
              </div>
              <FaLock className="text-4xl text-red-200" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-indigo-100 text-sm font-medium">Con Metas</div>
                <div className="text-3xl font-bold">{stats.usersWithGoals}</div>
              </div>
              <FaBullseye className="text-4xl text-indigo-200" />
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Acciones Rápidas</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <button
              onClick={handleCreateUser}
              className="flex flex-col items-center p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 hover:shadow-md transition-all group"
            >
              <FaPlus className="text-3xl text-blue-600 mb-3 group-hover:scale-110 transition-transform" />
              <span className="font-medium text-gray-800">Crear Usuario</span>
              <span className="text-sm text-gray-500">Agregar nuevo usuario al sistema</span>
            </button>
            
            <button
              onClick={() => setCurrentView('import')}
              className="flex flex-col items-center p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 hover:shadow-md transition-all group"
            >
              <FaUpload className="text-3xl text-green-600 mb-3 group-hover:scale-110 transition-transform" />
              <span className="font-medium text-gray-800">Importar Usuarios</span>
              <span className="text-sm text-gray-500">Cargar usuarios desde Excel</span>
            </button>
            
            <button
              onClick={() => setCurrentView('goals')}
              className="flex flex-col items-center p-6 bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg border border-purple-200 hover:shadow-md transition-all group"
            >
              <FaBullseye className="text-3xl text-purple-600 mb-3 group-hover:scale-110 transition-transform" />
              <span className="font-medium text-gray-800">Gestionar Metas</span>
              <span className="text-sm text-gray-500">Establecer objetivos y seguimiento</span>
            </button>
            
            <button
              onClick={() => setShowPasswordPolicyModal(true)}
              className="flex flex-col items-center p-6 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-200 hover:shadow-md transition-all group"
            >
              <FaShieldAlt className="text-3xl text-orange-600 mb-3 group-hover:scale-110 transition-transform" />
              <span className="font-medium text-gray-800">Políticas</span>
              <span className="text-sm text-gray-500">Configurar seguridad</span>
            </button>
            
            <button
              onClick={() => {
                if (window.confirm('¿Está seguro de que desea migrar los nombres de cursos? Esta acción actualizará automáticamente los cursos asignados de todos los usuarios.')) {
                  handleMigrateCourseNames();
                }
              }}
              className="flex flex-col items-center p-6 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg border border-yellow-200 hover:shadow-md transition-all group"
              disabled={loading}
            >
              <FaCog className="text-3xl text-yellow-600 mb-3 group-hover:scale-110 transition-transform" />
              <span className="font-medium text-gray-800">Migrar Cursos</span>
              <span className="text-sm text-gray-500">Corregir nombres de cursos</span>
            </button>
          </div>
        </div>

        {/* My Goals Widget for Teachers */}
        {UserService.getCurrentUser()?.role === 'teacher' && (() => {
          const currentUser = UserService.getCurrentUser();
          const currentUserData = users.find(u => u.username === currentUser?.username);
          const userGoals = currentUserData?.goals || [];
          const completedGoals = userGoals.filter(g => g.status === 'completed').length;
          const pendingGoals = userGoals.filter(g => g.status === 'pending').length;
          const inProgressGoals = userGoals.filter(g => g.status === 'in_progress').length;
          const overdueGoals = userGoals.filter(g => g.status === 'overdue').length;

          return (
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl shadow-lg p-6 text-white mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                    <FaBullseye className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Mis Metas</h3>
                    <p className="text-blue-100">Seguimiento de objetivos personales</p>
                  </div>
                </div>
                <button
                  onClick={() => setCurrentView('goals')}
                  className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <span>Ver Detalles</span>
                  <FaArrowRight className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold">{userGoals.length}</div>
                  <div className="text-blue-100 text-sm">Total</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-300">{completedGoals}</div>
                  <div className="text-blue-100 text-sm">Completadas</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-300">{inProgressGoals}</div>
                  <div className="text-blue-100 text-sm">En Progreso</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-300">{overdueGoals}</div>
                  <div className="text-blue-100 text-sm">Vencidas</div>
                </div>
              </div>

              {userGoals.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/20">
                  <div className="text-sm mb-2">Progreso General: {Math.round((completedGoals / userGoals.length) * 100)}%</div>
                  <div className="bg-white/20 rounded-full h-3">
                    <div 
                      className="bg-white rounded-full h-3 transition-all duration-500"
                      style={{ width: `${(completedGoals / userGoals.length) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {(() => {
                // Próximas 2 metas a vencer
                const upcomingGoals = userGoals
                  .filter(g => g.status !== 'completed' && g.targetDate)
                  .sort((a, b) => new Date(a.targetDate).getTime() - new Date(b.targetDate).getTime())
                  .slice(0, 2);

                if (upcomingGoals.length > 0) {
                  return (
                    <div className="mt-4 pt-4 border-t border-white/20">
                      <div className="text-sm font-medium mb-3 flex items-center">
                        <FaClock className="w-4 h-4 mr-2" />
                        Próximas a Vencer
                      </div>
                      <div className="space-y-2">
                        {upcomingGoals.map(goal => {
                          const daysUntilDue = Math.ceil((new Date(goal.targetDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                          const isOverdue = daysUntilDue < 0;
                          const isUrgent = daysUntilDue <= 3 && daysUntilDue >= 0;
                          
                          return (
                            <div key={goal.id} className="bg-white/10 rounded-lg p-3">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="font-medium text-sm truncate" title={goal.title}>
                                    {goal.title}
                                  </div>
                                  <div className="flex items-center space-x-2 mt-1">
                                    <span className={`text-xs px-2 py-1 rounded-full ${
                                      isOverdue 
                                        ? 'bg-red-400/20 text-red-200' 
                                        : isUrgent 
                                        ? 'bg-yellow-400/20 text-yellow-200'
                                        : 'bg-green-400/20 text-green-200'
                                    }`}>
                                      {isOverdue 
                                        ? `Vencida hace ${Math.abs(daysUntilDue)} días`
                                        : daysUntilDue === 0
                                        ? 'Vence hoy'
                                        : daysUntilDue === 1
                                        ? 'Vence mañana'
                                        : `${daysUntilDue} días restantes`
                                      }
                                    </span>
                                    <span className="text-xs text-blue-200">
                                      {goal.progress}% completado
                                    </span>
                                  </div>
                                </div>
                                {(isOverdue || isUrgent) && (
                                  <FaExclamationTriangle className={`w-4 h-4 ml-2 ${
                                    isOverdue ? 'text-red-300' : 'text-yellow-300'
                                  }`} />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                
                return null;
              })()}

              {userGoals.length === 0 && (
                <div className="text-center py-4 text-blue-100">
                  <FaBullseye className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No tienes metas asignadas aún</p>
                </div>
              )}
            </div>
          );
        })()}

        {/* Recent Users - Only for Admins */}
        {UserService.getCurrentUser()?.role === 'admin' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Usuarios Recientes</h3>
              <button
                onClick={() => setCurrentView('users')}
                className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
              >
                Ver todos →
              </button>
            </div>
            <div className="space-y-3">
              {(users || []).slice(0, 5).map(user => (
                <div key={getUserId(user)} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                      user.role === 'admin' ? 'bg-purple-500' : 'bg-blue-500'
                    }`}>
                      {user.firstName ? user.firstName[0] : (user.username || 'U')[0]}
                    </div>
                    <div>
                      <div className="font-medium text-gray-800">
                        {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : (user.username || 'Usuario')}
                      </div>
                      <div className="text-sm text-gray-500">{user.email || 'Sin email'}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      user.role === 'admin' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {user.role === 'admin' ? 'Admin' : 'Profesor'}
                    </span>
                    <span className={`w-2 h-2 rounded-full ${
                      user.isActive ? 'bg-green-500' : 'bg-red-500'
                    }`}></span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // VISTA DE ACTUALIZACIÓN ANUAL
  // ============================================
  const renderAnnualUpdateView = () => {
    // Helper function to get current step based on active tab
    const currentStep = annualUpdateTab === 'teachers' ? annualUpdateStep : studentAnnualUpdateStep;
    const isInProgress = currentStep !== 'init' && currentStep !== 'complete';
    
    return (
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center">
                <FaSync className="mr-3" />
                Actualización Anual
              </h1>
              <p className="mt-2 text-indigo-100">
                Sistema automatizado para migración de datos de un año escolar a otro
              </p>
            </div>
            {isInProgress && (
              <button
                onClick={annualUpdateTab === 'teachers' ? resetAnnualUpdate : resetStudentAnnualUpdate}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <FaTimes className="inline mr-2" />
                Cancelar
              </button>
            )}
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="bg-white rounded-lg border border-gray-200 p-2">
          <div className="flex space-x-2">
            <button
              onClick={() => setAnnualUpdateTab('teachers')}
              className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center ${
                annualUpdateTab === 'teachers'
                  ? 'bg-indigo-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <FaUserTie className="mr-2" />
              Docentes
            </button>
            <button
              onClick={() => setAnnualUpdateTab('students')}
              className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center ${
                annualUpdateTab === 'students'
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <FaGraduationCap className="mr-2" />
              Estudiantes
            </button>
          </div>
        </div>

        {/* Content based on selected tab */}
        {annualUpdateTab === 'teachers' ? renderTeacherAnnualUpdate() : renderStudentAnnualUpdate()}
      </div>
    );
  };

  // Render Teacher Annual Update content
  const renderTeacherAnnualUpdate = () => {
    return (
      <>
        {/* Paso Indicador */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className={`flex items-center ${annualUpdateStep === 'init' || annualUpdateStep === 'upload' ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2">1</div>
              <span>Cargar</span>
            </div>
            <div className="flex-1 h-1 bg-gray-200 mx-4"></div>
            <div className={`flex items-center ${annualUpdateStep === 'review' ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2">2</div>
              <span>Revisar</span>
            </div>
            <div className="flex-1 h-1 bg-gray-200 mx-4"></div>
            <div className={`flex items-center ${annualUpdateStep === 'confirm' ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2">3</div>
              <span>Confirmar</span>
            </div>
            <div className="flex-1 h-1 bg-gray-200 mx-4"></div>
            <div className={`flex items-center ${annualUpdateStep === 'processing' || annualUpdateStep === 'complete' ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2">4</div>
              <span>Ejecutar</span>
            </div>
          </div>
        </div>

        {/* PASO 1: INICIO */}
        {annualUpdateStep === 'init' && (
          <div className="space-y-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex items-start">
                <FaExclamationTriangle className="text-yellow-500 text-2xl mt-1 mr-3" />
                <div>
                  <h3 className="text-yellow-800 font-semibold text-lg">⚠️ Proceso Crítico</h3>
                  <p className="text-yellow-700 mt-2">
                    Esta operación realizará cambios masivos en la base de datos. Asegúrate de:
                  </p>
                  <ul className="list-disc list-inside text-yellow-700 mt-2 space-y-1">
                    <li>Tener un backup externo actualizado de la base de datos</li>
                    <li>Coordinar con el equipo técnico antes de ejecutar</li>
                    <li>Ejecutar en horario de bajo tráfico</li>
                    <li>Verificar que los datos nuevos estén correctos</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Configuración de Actualización</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="font-medium text-gray-700">Preservar Administradores</label>
                    <p className="text-sm text-gray-500">Los usuarios con rol admin no serán modificados</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={annualUpdateConfig.preserveAdmins}
                    onChange={(e) => setAnnualUpdateConfig({...annualUpdateConfig, preserveAdmins: e.target.checked})}
                    className="w-5 h-5"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="font-medium text-gray-700">Desactivar Usuarios Antiguos</label>
                    <p className="text-sm text-gray-500">Los usuarios antiguos serán desactivados (recomendado)</p>
                  </div>
                  <input
                    type="radio"
                    name="userAction"
                    checked={annualUpdateConfig.deactivateOldUsers && !annualUpdateConfig.deleteOldUsers}
                    onChange={() => setAnnualUpdateConfig({...annualUpdateConfig, deactivateOldUsers: true, deleteOldUsers: false})}
                    className="w-5 h-5"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div>
                    <label className="font-medium text-red-700">Eliminar Usuarios Antiguos</label>
                    <p className="text-sm text-red-600">⚠️ Los usuarios antiguos serán eliminados permanentemente</p>
                  </div>
                  <input
                    type="radio"
                    name="userAction"
                    checked={annualUpdateConfig.deleteOldUsers}
                    onChange={() => setAnnualUpdateConfig({...annualUpdateConfig, deactivateOldUsers: false, deleteOldUsers: true})}
                    className="w-5 h-5"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-between">
                <div className="text-gray-600">
                  <p className="font-medium">Usuarios actuales: {users.length}</p>
                  <p className="text-sm">Profesores: {users.filter(u => u.role === 'teacher').length} | Administradores: {users.filter(u => u.role === 'admin').length}</p>
                </div>
                <button
                  onClick={() => setAnnualUpdateStep('upload')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center space-x-2"
                >
                  <FaUpload />
                  <span>Continuar a Carga de Archivo</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PASO 3: REVISIÓN DE CAMBIOS */}
        {annualUpdateStep === 'review' && (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
              <FaCheckCircle className="text-green-500 text-xl mr-3" />
              <div>
                <p className="font-semibold text-green-800">Archivo procesado exitosamente</p>
                <p className="text-sm text-green-700">{newUsersData.length} usuarios detectados en el archivo</p>
              </div>
            </div>

            {/* Resumen de cambios */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-2 flex items-center">
                  <FaUserPlus className="mr-2" />
                  Usuarios Nuevos
                </h4>
                <p className="text-3xl font-bold text-green-600">{usersToCreate.length}</p>
                <p className="text-sm text-green-700">Serán creados</p>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-orange-800 mb-2 flex items-center">
                  <FaEdit className="mr-2" />
                  Con Cambios
                </h4>
                <p className="text-3xl font-bold text-orange-600">{usersToUpdate.length}</p>
                <p className="text-sm text-orange-700">Serán actualizados</p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
                  <FaCheck className="mr-2" />
                  Sin Cambios
                </h4>
                <p className="text-3xl font-bold text-blue-600">{usersToMaintain.length}</p>
                <p className="text-sm text-blue-700">Se mantienen igual</p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-800 mb-2 flex items-center">
                  <FaUserMinus className="mr-2" />
                  A {annualUpdateConfig.deleteOldUsers ? 'Eliminar' : 'Desactivar'}
                </h4>
                <p className="text-3xl font-bold text-red-600">{usersToDeactivate.length}</p>
                <p className="text-sm text-red-700">No están en archivo</p>
              </div>
            </div>

            {/* Detalles de usuarios con cambios */}
            {usersToUpdate.length > 0 && (
              <div className="bg-white rounded-lg border border-orange-200 p-6">
                <h4 className="font-semibold text-gray-800 mb-3">✏️ Usuarios con Cambios ({usersToUpdate.length})</h4>
                <div className="max-h-96 overflow-y-auto bg-gray-50 rounded-lg p-4 space-y-3">
                  {usersToUpdate.map(({user, newData, changes}, index) => (
                    <div key={index} className="bg-white p-4 rounded-lg border border-orange-200">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className="font-medium text-gray-800">{user.firstName} {user.lastName}</span>
                          <span className="text-gray-500 text-sm ml-2">({user.email})</span>
                        </div>
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded whitespace-nowrap">
                          {changes.length} cambio{changes.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="mt-2 space-y-1">
                        {changes.map((change, idx) => (
                          <div key={idx} className="text-sm text-gray-600 flex items-start">
                            <span className="text-orange-500 mr-2">→</span>
                            <span>{change}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Detalles de usuarios nuevos */}
            {usersToCreate.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h4 className="font-semibold text-gray-800 mb-3">📥 Usuarios Nuevos ({usersToCreate.length})</h4>
                <div className="max-h-60 overflow-y-auto bg-gray-50 rounded-lg p-4 space-y-2">
                  {usersToCreate.map((user, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b border-gray-200">
                      <div>
                        <span className="font-medium">{user.firstName} {user.lastName}</span>
                        <span className="text-gray-500 text-sm ml-2">({user.username})</span>
                      </div>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Nuevo</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Detalles de usuarios a desactivar/eliminar */}
            {usersToDeactivate.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h4 className="font-semibold text-gray-800 mb-3">⚠️ Usuarios a {annualUpdateConfig.deleteOldUsers ? 'Eliminar' : 'Desactivar'} ({usersToDeactivate.length})</h4>
                <div className="max-h-60 overflow-y-auto bg-gray-50 rounded-lg p-4 space-y-2">
                  {usersToDeactivate.map(user => (
                    <div key={getUserId(user)} className="flex items-center justify-between py-2 border-b border-gray-200">
                      <div>
                        <span className="font-medium">{user.firstName} {user.lastName}</span>
                        <span className="text-gray-500 text-sm ml-2">({user.username})</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${annualUpdateConfig.deleteOldUsers ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {annualUpdateConfig.deleteOldUsers ? 'Eliminar' : 'Desactivar'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Botones de acción */}
            <div className="flex justify-between">
              <button
                onClick={() => setAnnualUpdateStep('upload')}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                ← Volver a Cargar Archivo
              </button>
              <button
                onClick={() => setAnnualUpdateStep('confirm')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Continuar a Confirmación →
              </button>
            </div>
          </div>
        )}

        {/* PASO 2: CARGA DE ARCHIVO */}
        {annualUpdateStep === 'upload' && (
          <div className="space-y-6">
            {/* Botón para descargar plantilla */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                    <FaFileExcel className="mr-2 text-green-600" />
                    Descarga la Plantilla con Usuarios Actuales
                  </h4>
                  <p className="text-sm text-gray-700 mb-3">
                    Te recomendamos descargar la plantilla Excel con los datos actuales para usarla como base. 
                    Así solo tienes que agregar o eliminar usuarios sin tener que crear todo desde cero.
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 mb-4">
                    <li>✓ Incluye todos los usuarios actuales con sus datos</li>
                    <li>✓ Formato correcto ya aplicado</li>
                    <li>✓ Solo edita, agrega o elimina filas según necesites</li>
                    {annualUpdateConfig.preserveAdmins && (
                      <li>✓ Los administradores no se incluyen (se preservan automáticamente)</li>
                    )}
                  </ul>
                </div>
                <button
                  onClick={downloadCurrentUsersTemplate}
                  className="ml-4 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center space-x-2 shadow-md"
                >
                  <FaDownload />
                  <span>Descargar Plantilla</span>
                </button>
              </div>
            </div>

            {/* Carga de archivo */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Cargar Archivo Excel de Usuarios</h3>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-blue-800 mb-2">Formato del archivo Excel:</h4>
                <div className="bg-white rounded p-3 mb-3 overflow-x-auto">
                  <table className="w-full text-xs text-blue-700">
                    <thead>
                      <tr className="border-b border-blue-200">
                        <th className="text-left p-1 whitespace-nowrap">Usuario</th>
                        <th className="text-left p-1 whitespace-nowrap">Correo Electrónico</th>
                        <th className="text-left p-1 whitespace-nowrap">Nombre</th>
                        <th className="text-left p-1 whitespace-nowrap">Apellido</th>
                        <th className="text-left p-1 whitespace-nowrap">Rol</th>
                        <th className="text-left p-1 whitespace-nowrap">Cursos Asignados</th>
                        <th className="text-left p-1 whitespace-nowrap">Dimensiones Observables</th>
                        <th className="text-left p-1 whitespace-nowrap">Contraseña</th>
                        <th className="text-left p-1 whitespace-nowrap">Teléfono</th>
                        <th className="text-left p-1 whitespace-nowrap">Contacto de Emergencia</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="text-gray-600">
                        <td className="p-1 whitespace-nowrap">jperez</td>
                        <td className="p-1 whitespace-nowrap">j.perez@cpv.cl</td>
                        <td className="p-1 whitespace-nowrap">Juan</td>
                        <td className="p-1 whitespace-nowrap">Pérez</td>
                        <td className="p-1 whitespace-nowrap">Profesor</td>
                        <td className="p-1 whitespace-nowrap">Kinder A;Kinder B</td>
                        <td className="p-1 whitespace-nowrap text-xs">Autoconciencia y voluntad;Habilidades pensamiento</td>
                        <td className="p-1 whitespace-nowrap">(vacío)</td>
                        <td className="p-1 whitespace-nowrap">+56912345678</td>
                        <td className="p-1 whitespace-nowrap">+56987654321</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="text-sm text-blue-700 space-y-1">
                  <p>📋 <strong>Instrucciones:</strong></p>
                  <p>• Descarga la plantilla con usuarios actuales y modifícala según necesites</p>
                  <p>• Cursos separados por punto y coma (;)</p>
                  <p>• Dimensiones: nombres completos separados por punto y coma (;)</p>
                  <p className="ml-4 text-xs">Ej: Autoconciencia y voluntad;Habilidades pensamiento</p>
                  <p>• Rol: "Administrador" o "Profesor"</p>
                  <p>• Contraseña: dejar vacío para generar automáticamente</p>
                  <p>• El sistema comparará automáticamente con los usuarios actuales</p>
                </div>
              </div>

              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleNewUsersFileUpload}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-3 file:px-6
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-600 file:text-white
                  hover:file:bg-blue-700
                  cursor-pointer"
              />
              <p className="mt-2 text-xs text-gray-500">
                Solo se aceptan archivos Excel (.xlsx, .xls)
              </p>
            </div>
          </div>
        )}

        {/* PASO 4: CONFIRMACIÓN */}
        {annualUpdateStep === 'confirm' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Confirmar Actualización</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-2">Nuevos Usuarios</h4>
                  <p className="text-3xl font-bold text-green-600">{usersToCreate.length}</p>
                  <p className="text-sm text-green-700">Serán creados</p>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-semibold text-orange-800 mb-2">Con Cambios</h4>
                  <p className="text-3xl font-bold text-orange-600">{usersToUpdate.length}</p>
                  <p className="text-sm text-orange-700">Serán actualizados</p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">Sin Cambios</h4>
                  <p className="text-3xl font-bold text-blue-600">{usersToMaintain.length}</p>
                  <p className="text-sm text-blue-700">Se mantienen</p>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-800 mb-2">A Procesar</h4>
                  <p className="text-3xl font-bold text-red-600">{usersToDeactivate.length}</p>
                  <p className="text-sm text-red-700">
                    Serán {annualUpdateConfig.deleteOldUsers ? 'eliminados' : 'desactivados'}
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-yellow-800 font-semibold mb-2">
                  ⚠️ ¿Estás seguro de continuar?
                </p>
                <p className="text-yellow-700 text-sm">
                  • Se generará un backup automático antes de ejecutar<br/>
                  • Esta acción NO se puede deshacer después de ejecutarse<br/>
                  • Asegúrate de revisar bien los cambios antes de continuar
                </p>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={() => setAnnualUpdateStep('review')}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  ← Volver a Revisar
                </button>
                <button
                  onClick={executeAnnualUpdate}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Confirmar y Ejecutar Actualización
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PASO 5: PROCESANDO */}
        {annualUpdateStep === 'processing' && annualUpdateProgress && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Procesando Actualización...</h3>
              
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>{annualUpdateProgress.phase}</span>
                  <span>{annualUpdateProgress.current} / {annualUpdateProgress.total}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                    style={{ width: `${(annualUpdateProgress.current / annualUpdateProgress.total) * 100}%` }}
                  ></div>
                </div>
              </div>

              {annualUpdateProgress.currentItem && (
                <p className="text-gray-700 mb-4">
                  <FaSpinner className="inline animate-spin mr-2" />
                  Procesando: {annualUpdateProgress.currentItem}
                </p>
              )}

              {annualUpdateProgress.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-800 mb-2">Errores encontrados:</h4>
                  <ul className="text-sm text-red-700 space-y-1">
                    {annualUpdateProgress.errors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* PASO 6: COMPLETADO */}
        {annualUpdateStep === 'complete' && (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center">
                <FaCheckCircle className="text-green-500 text-4xl mr-4" />
                <div>
                  <h3 className="text-2xl font-bold text-green-800">Actualización Completada</h3>
                  <p className="text-green-700">La migración de usuarios se ha completado exitosamente</p>
                </div>
              </div>
            </div>

            {annualUpdateProgress && annualUpdateProgress.errors.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 mb-2">
                  Se completó con {annualUpdateProgress.errors.length} errores:
                </h4>
                <ul className="text-sm text-yellow-700 space-y-1 max-h-40 overflow-y-auto">
                  {annualUpdateProgress.errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Resumen</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Usuarios Procesados</p>
                  <p className="text-2xl font-bold text-gray-800">{usersToDeactivate.length}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Usuarios Actualizados</p>
                  <p className="text-2xl font-bold text-gray-800">{usersToUpdate.length}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Nuevos Usuarios</p>
                  <p className="text-2xl font-bold text-gray-800">{usersToCreate.length}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Total Actual</p>
                  <p className="text-2xl font-bold text-gray-800">{users.length}</p>
                </div>
              </div>

              <div className="mt-6 flex space-x-4">
                <button
                  onClick={() => setCurrentView('users')}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Ir a Lista de Usuarios
                </button>
                <button
                  onClick={resetAnnualUpdate}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Nueva Actualización
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // Render Student Annual Update content
  const renderStudentAnnualUpdate = () => {
    return (
      <>
        {/* Step Indicator */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className={`flex items-center ${studentAnnualUpdateStep === 'init' || studentAnnualUpdateStep === 'upload' ? 'text-green-600 font-semibold' : 'text-gray-400'}`}>
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-2">1</div>
              <span>Cargar</span>
            </div>
            <div className="flex-1 h-1 bg-gray-200 mx-4"></div>
            <div className={`flex items-center ${studentAnnualUpdateStep === 'review' ? 'text-green-600 font-semibold' : 'text-gray-400'}`}>
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-2">2</div>
              <span>Revisar</span>
            </div>
            <div className="flex-1 h-1 bg-gray-200 mx-4"></div>
            <div className={`flex items-center ${studentAnnualUpdateStep === 'confirm' ? 'text-green-600 font-semibold' : 'text-gray-400'}`}>
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-2">3</div>
              <span>Confirmar</span>
            </div>
            <div className="flex-1 h-1 bg-gray-200 mx-4"></div>
            <div className={`flex items-center ${studentAnnualUpdateStep === 'processing' || studentAnnualUpdateStep === 'complete' ? 'text-green-600 font-semibold' : 'text-gray-400'}`}>
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-2">4</div>
              <span>Ejecutar</span>
            </div>
          </div>
        </div>

        {/* PASO 1: INICIO */}
        {studentAnnualUpdateStep === 'init' && (
          <div className="space-y-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex items-start">
                <FaExclamationTriangle className="text-yellow-500 text-xl mr-3 mt-1" />
                <div>
                  <h3 className="font-semibold text-yellow-800">Antes de comenzar</h3>
                  <p className="text-yellow-700 text-sm mt-1">
                    Este proceso actualizará los datos de estudiantes para el nuevo año escolar. 
                    Se guardarán los puntajes actuales como "nivel del año anterior" y se actualizarán los cursos automáticamente.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Proceso de Actualización</h3>
              
              <div className="mb-6 p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                <label className="block text-sm font-semibold text-indigo-900 mb-2">
                  <FaCalendarAlt className="inline mr-2" />
                  Año Académico a Cerrar:
                </label>
                <div className="flex items-center space-x-4">
                  <select
                    value={selectedAcademicYear}
                    onChange={(e) => setSelectedAcademicYear(e.target.value)}
                    className="flex-1 p-2.5 bg-white border border-indigo-300 rounded-lg text-indigo-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-medium"
                  >
                    <option value="2024">2024</option>
                    <option value="2025">2025 (Cierre actual)</option>
                    <option value="2026">2026</option>
                  </select>
                  <div className="text-xs text-indigo-700 max-w-[200px]">
                    Los puntajes de este año se guardarán como histórico para el estudiante.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                      <span className="text-green-600 font-semibold">1</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">1. Descargar Plantilla (Opcional)</h4>
                      <p className="text-sm text-gray-600">Obtén el archivo Excel con los estudiantes actuales si necesitas una base</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                      <span className="text-green-600 font-semibold">2</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">2. Modificar y Cargar</h4>
                      <p className="text-sm text-gray-600">Edita los cursos en el archivo y cárgalo al sistema</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                      <span className="text-green-600 font-semibold">3</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">3. Revisar Cambios</h4>
                      <p className="text-sm text-gray-600">Verifica las actualizaciones antes de aplicar</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                      <span className="text-green-600 font-semibold">4</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">4. Ejecutar</h4>
                      <p className="text-sm text-gray-600">Aplica los cambios con respaldo automático</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 border-t pt-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={downloadStudentsTemplate}
                    disabled={loading}
                    className="flex-1 bg-green-50 hover:bg-green-100 text-green-700 border-2 border-green-200 px-6 py-4 rounded-xl font-bold transition-all flex items-center justify-center group"
                  >
                    {loading ? (
                      <>
                        <FaSpinner className="animate-spin mr-2" />
                        Preparando...
                      </>
                    ) : (
                      <>
                        <FaDownload className="mr-2 group-hover:scale-110 transition-transform" />
                        Descargar Plantilla (Opcional)
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setStudentAnnualUpdateStep('upload')}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white px-6 py-4 rounded-xl font-bold transition-all flex items-center justify-center shadow-lg hover:shadow-xl group"
                  >
                    <FaUpload className="mr-2 group-hover:scale-110 transition-transform" />
                    Ir a Cargar Archivo
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">
                <FaGraduationCap className="inline mr-2" />
                Qué sucederá con los estudiantes:
              </h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• <strong>Existentes en Excel:</strong> Se actualizará su curso y se guardarán sus puntajes como "año anterior"</li>
                <li>• <strong>Nuevos en Excel:</strong> Se crearán con habilidades vacías para completar</li>
                <li>• <strong>No incluidos en Excel:</strong> Se retirarán conservando su historial</li>
                <li>• <strong>Puntajes:</strong> Se guardarán en el historial para consulta futura</li>
              </ul>
            </div>
          </div>
        )}

        {/* PASO 2: CARGA DE ARCHIVO */}
        {studentAnnualUpdateStep === 'upload' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                <FaUpload className="inline mr-2 text-green-600" />
                Cargar Archivo de Estudiantes
              </h3>
              <p className="text-gray-600 mb-4">
                Sube el archivo Excel modificado con los datos del nuevo año escolar.
                Asegúrate de que el "Curso Año Siguiente" esté correcto para cada estudiante.
              </p>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-green-500 transition-colors">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleStudentFileUpload}
                  className="hidden"
                  id="student-file-upload"
                />
                <label
                  htmlFor="student-file-upload"
                  className="cursor-pointer"
                >
                  <FaFileExcel className="text-6xl text-green-500 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-700">
                    Haz clic para seleccionar el archivo
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Formatos aceptados: .xlsx, .xls
                  </p>
                </label>
              </div>

              <div className="mt-4 flex justify-between">
                <button
                  onClick={resetStudentAnnualUpdate}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg transition-colors"
                >
                  <FaArrowRight className="inline mr-2 rotate-180" />
                  Volver
                </button>
                <button
                  onClick={downloadStudentsTemplate}
                  disabled={loading}
                  className="bg-green-100 hover:bg-green-200 text-green-800 px-4 py-2 rounded-lg transition-colors border border-green-200"
                >
                  <FaDownload className="inline mr-2" />
                  Descargar plantilla
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PASO 3: REVISIÓN DE CAMBIOS */}
        {studentAnnualUpdateStep === 'review' && (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
              <FaCheckCircle className="text-green-500 text-xl mr-3" />
              <div>
                <h3 className="font-semibold text-green-800">Archivo procesado correctamente</h3>
                <p className="text-sm text-green-700">Revisa los cambios detectados antes de aplicarlos</p>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600">A Actualizar</p>
                    <p className="text-3xl font-bold text-blue-800">{studentsToUpdate.length}</p>
                  </div>
                  <FaSync className="text-3xl text-blue-300" />
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">Nuevos</p>
                    <p className="text-3xl font-bold text-green-800">{studentsToCreate.length}</p>
                  </div>
                  <FaUserPlus className="text-3xl text-green-300" />
                </div>
              </div>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600">A Retirar</p>
                    <p className="text-3xl font-bold text-red-800">{studentsToWithdraw.length}</p>
                  </div>
                  <FaUserMinus className="text-3xl text-red-300" />
                </div>
              </div>
            </div>

            {/* Students to Update */}
            {studentsToUpdate.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <FaSync className="text-blue-500 mr-2" />
                  Estudiantes a Actualizar ({studentsToUpdate.length})
                </h4>
                <div className="max-h-60 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">ID</th>
                        <th className="px-3 py-2 text-left">Nombre</th>
                        <th className="px-3 py-2 text-left">Curso Actual</th>
                        <th className="px-3 py-2 text-left">Nuevo Curso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {studentsToUpdate.slice(0, 50).map((student, index) => (
                        <tr key={index} className="border-t">
                          <td className="px-3 py-2">{student.id}</td>
                          <td className="px-3 py-2">{student.newName || student.currentName}</td>
                          <td className="px-3 py-2">{student.currentCourse}</td>
                          <td className="px-3 py-2 text-blue-600 font-medium">{student.newCourse}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {studentsToUpdate.length > 50 && (
                    <p className="text-sm text-gray-500 p-2">...y {studentsToUpdate.length - 50} más</p>
                  )}
                </div>
              </div>
            )}

            {/* New Students */}
            {studentsToCreate.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <FaUserPlus className="text-green-500 mr-2" />
                  Estudiantes Nuevos ({studentsToCreate.length})
                </h4>
                <div className="max-h-40 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">ID</th>
                        <th className="px-3 py-2 text-left">Nombre</th>
                        <th className="px-3 py-2 text-left">Curso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {studentsToCreate.slice(0, 20).map((student, index) => (
                        <tr key={index} className="border-t">
                          <td className="px-3 py-2">{student.id}</td>
                          <td className="px-3 py-2">{student.name}</td>
                          <td className="px-3 py-2 text-green-600">{student.course}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Students to Withdraw */}
            {studentsToWithdraw.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <FaUserMinus className="text-red-500 mr-2" />
                  Estudiantes a Retirar ({studentsToWithdraw.length})
                </h4>
                <div className="max-h-40 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">ID</th>
                        <th className="px-3 py-2 text-left">Nombre</th>
                        <th className="px-3 py-2 text-left">Curso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {studentsToWithdraw.slice(0, 20).map((student, index) => (
                        <tr key={index} className="border-t">
                          <td className="px-3 py-2">{student.id}</td>
                          <td className="px-3 py-2">{student.name}</td>
                          <td className="px-3 py-2 text-red-600">{student.course}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <button
                onClick={resetStudentAnnualUpdate}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => setStudentAnnualUpdateStep('confirm')}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Continuar a Confirmación
                <FaArrowRight className="inline ml-2" />
              </button>
            </div>
          </div>
        )}

        {/* PASO 4: CONFIRMACIÓN */}
        {studentAnnualUpdateStep === 'confirm' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="text-center mb-6">
                <FaExclamationTriangle className="text-yellow-500 text-5xl mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-800">Confirmar Actualización</h3>
                <p className="text-gray-600 mt-2">
                  Estás a punto de aplicar los siguientes cambios. Esta acción creará un backup automático.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{studentsToUpdate.length}</p>
                  <p className="text-sm text-blue-800">Estudiantes a actualizar</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{studentsToCreate.length}</p>
                  <p className="text-sm text-green-800">Nuevos estudiantes</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-red-600">{studentsToWithdraw.length}</p>
                  <p className="text-sm text-red-800">Estudiantes a retirar</p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-yellow-800 mb-2">Acciones que se realizarán:</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>✓ Se creará un backup completo de todos los estudiantes</li>
                  <li>✓ Los puntajes actuales se guardarán como "nivel del año anterior"</li>
                  <li>✓ Se guardará el historial completo en la base de datos</li>
                  <li>✓ Los cursos de estudiantes existentes se actualizarán</li>
                  <li>✓ Los estudiantes nuevos se crearán con habilidades vacías</li>
                  <li>✓ Los estudiantes no incluidos se moverán a "retirados"</li>
                </ul>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStudentAnnualUpdateStep('review')}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Volver
                </button>
                <button
                  onClick={executeStudentAnnualUpdate}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
                >
                  <FaCheck className="inline mr-2" />
                  Ejecutar Actualización
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PASO 5: PROCESANDO */}
        {studentAnnualUpdateStep === 'processing' && studentAnnualUpdateProgress && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="text-center mb-6">
                <FaSpinner className="animate-spin text-green-500 text-5xl mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-800">Procesando Actualización...</h3>
                <p className="text-gray-600 mt-2">{studentAnnualUpdateProgress.phase}</p>
              </div>

              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>{studentAnnualUpdateProgress.currentItem}</span>
                  <span>{studentAnnualUpdateProgress.current} / {studentAnnualUpdateProgress.total}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div 
                    className="bg-green-600 rounded-full h-4 transition-all duration-300"
                    style={{ width: `${(studentAnnualUpdateProgress.current / studentAnnualUpdateProgress.total) * 100}%` }}
                  ></div>
                </div>
              </div>

              {studentAnnualUpdateProgress.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-800 mb-2">Errores encontrados:</h4>
                  <ul className="text-sm text-red-700 space-y-1">
                    {studentAnnualUpdateProgress.errors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* PASO 6: COMPLETADO */}
        {studentAnnualUpdateStep === 'complete' && (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center">
                <FaCheckCircle className="text-green-500 text-4xl mr-4" />
                <div>
                  <h3 className="text-2xl font-bold text-green-800">Actualización Completada</h3>
                  <p className="text-green-700">La migración de estudiantes se ha completado exitosamente</p>
                </div>
              </div>
            </div>

            {studentAnnualUpdateProgress && studentAnnualUpdateProgress.errors.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 mb-2">
                  Se completó con {studentAnnualUpdateProgress.errors.length} errores:
                </h4>
                <ul className="text-sm text-yellow-700 space-y-1 max-h-40 overflow-y-auto">
                  {studentAnnualUpdateProgress.errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Resumen</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-600">Actualizados</p>
                  <p className="text-2xl font-bold text-blue-800">{studentsToUpdate.length}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-600">Creados</p>
                  <p className="text-2xl font-bold text-green-800">{studentsToCreate.length}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <p className="text-sm text-red-600">Retirados</p>
                  <p className="text-2xl font-bold text-red-800">{studentsToWithdraw.length}</p>
                </div>
              </div>

              <div className="mt-6 flex space-x-4">
                <button
                  onClick={() => setCurrentView('estudiantes')}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Ir a Gestión de Estudiantes
                </button>
                <button
                  onClick={resetStudentAnnualUpdate}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Nueva Actualización
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4 rounded-2xl mr-4 shadow-lg">
                <FaUsers className="text-white text-2xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Gestión de Usuarios</h1>
                <p className="text-gray-600 mt-1">Administrar usuarios, permisos, metas y dimensiones del sistema</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 bg-white rounded-lg px-3 py-2 shadow-sm border">
                {connectionStatus === 'connected' && (
                  <>
                    <FaWifi className="text-green-500" />
                    <span className="text-sm text-green-600 font-medium">Conectado</span>
                  </>
                )}
                {connectionStatus === 'disconnected' && (
                  <>
                    <FaTimes className="text-red-500" />
                    <span className="text-sm text-red-600 font-medium">Sin conexión</span>
                  </>
                )}
                {connectionStatus === 'checking' && (
                  <>
                    <FaSpinner className="text-blue-500 animate-spin" />
                    <span className="text-sm text-blue-600 font-medium">Verificando...</span>
                  </>
                )}
              </div>
              
              <button
                onClick={loadUsers}
                disabled={loading}
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center space-x-2 transition-colors shadow-sm"
              >
                <FaSync className={loading ? 'animate-spin' : ''} />
                <span>Actualizar</span>
              </button>
            </div>
          </div>

          {/* Messages */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3 mb-4">
              <FaExclamationTriangle className="text-red-500 flex-shrink-0" />
              <span className="text-red-700 flex-1">{error}</span>
              <button
                onClick={() => setError(null)}
                className="text-red-500 hover:text-red-700 flex-shrink-0"
              >
                <FaTimes />
              </button>
            </div>
          )}

          {success && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3 mb-4">
              <FaCheck className="text-green-500 flex-shrink-0" />
              <span className="text-green-700 flex-1">{success}</span>
              <button
                onClick={() => setSuccess(null)}
                className="text-green-500 hover:text-green-700 flex-shrink-0"
              >
                <FaTimes />
              </button>
            </div>
          )}
        </div>

        {/* Navigation Tabs */}
        {renderNavigationTabs()}

        {/* Content based on current view */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 min-h-[600px]">
          {/* ELIMINADO: Vista General */}
          {/* {currentView === 'overview' && renderOverviewDashboard()} */}
          {currentView === 'users' && renderUsersView()}
          {currentView === 'estudiantes' && (
            <div className="p-6">
              <Retiro
                students={propStudents}
                onSectionChange={(section) => {
                  // Esta función se puede usar para navegar entre secciones si es necesario
                  console.log('Navegando a sección:', section);
                }}
              />
            </div>
          )}
          
          {currentView === 'informes-retiros' && (
            <div className="p-6">
              <InformeNotasRetiros
                students={propStudents}
                onSectionChange={(section) => {
                  console.log('Navegando a sección:', section);
                }}
              />
            </div>
          )}
          {/* OCULTO: Sección de Metas */}
          {/* {currentView === 'goals' && renderGoalsView()} */}
          {/* ELIMINADO: Configurar Skill Levels */}
          {/* {currentView === 'skill-levels' && renderSkillLevelsView()} */}
          {/* Sección Importar eliminada */}
          {/* Sección Backup eliminada */}
          {currentView === 'annual-update' && renderAnnualUpdateView()}
          {currentView === 'teachers-reports' && (
            <TeacherReportSettings />
          )}
        </div>

        {/* User Create/Edit Modal */}
        {(showCreateModal || showEditModal) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-semibold text-gray-800">
                  {selectedUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
                </h2>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Error Message */}
                {modalError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
                    <FaExclamationTriangle className="text-red-500 flex-shrink-0" />
                    <p className="text-red-700 text-sm font-medium">{modalError}</p>
                    <button 
                      onClick={() => setModalError(null)}
                      className="ml-auto text-red-500 hover:text-red-700"
                    >
                      <FaTimes />
                    </button>
                  </div>
                )}

                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre de Usuario *
                    </label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="usuario@ejemplo.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="email@ejemplo.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre *
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Nombre"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Apellido *
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Apellido"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rol
                    </label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as 'admin' | 'teacher' }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="teacher">👨‍🏫 Profesor</option>
                      <option value="admin">👑 Administrador</option>
                    </select>
                  </div>
                </div>

                {/* Password Section */}
                {!selectedUser ? (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-800">Contraseña</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Contraseña *
                        </label>
                        <input
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Contraseña"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confirmar Contraseña *
                        </label>
                        <input
                          type="password"
                          value={formData.confirmPassword}
                          onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Confirmar contraseña"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  // Sección de cambio de contraseña para usuarios existentes
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-800">Cambiar Contraseña (Opcional)</h3>
                    
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-yellow-800">
                        💡 <strong>Opcional:</strong> Deja estos campos vacíos si no quieres cambiar la contraseña
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nueva Contraseña
                        </label>
                        <input
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Nueva contraseña (opcional)"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confirmar Nueva Contraseña
                        </label>
                        <input
                          type="password"
                          value={formData.confirmPassword}
                          onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Confirmar nueva contraseña"
                        />
                      </div>
                    </div>
                  </div>
                )}



                {/* Course Permissions - Full Width */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <label className="text-lg font-semibold text-gray-800 flex items-center">
                      <FaGraduationCap className="mr-2 text-blue-600" />
                      Cursos Asignados 
                      <span className="ml-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        {formData.allowedCourses.length} de {courses.length}
                      </span>
                      {formData.allowedCourses.length > 0 && (
                        <span className="ml-2 text-xs text-gray-500">
                          ({formData.allowedCourses.slice(0, 3).map(course => getCompactCourseName(course)).join(', ')}
                          {formData.allowedCourses.length > 3 ? '...' : ''})
                        </span>
                      )}
                    </label>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, allowedCourses: courses }))}
                        className="flex items-center space-x-1 text-sm bg-blue-50 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
                      >
                        <FaCheck className="text-xs" />
                        <span>Todos</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, allowedCourses: [] }))}
                        className="flex items-center space-x-1 text-sm bg-gray-50 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
                      >
                        <FaTimes className="text-xs" />
                        <span>Limpiar</span>
                      </button>
                    </div>
                  </div>
                  
                  <div className="border-2 border-gray-200 rounded-lg p-3 bg-gradient-to-br from-gray-50 to-white">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 max-h-48 overflow-y-auto">
                      {courses.map(course => {
                        const isSelected = formData.allowedCourses.includes(course);
                        return (
                          <label 
                            key={course} 
                            className={`flex items-center p-2 rounded-lg cursor-pointer transition-all duration-200 border ${
                              isSelected 
                                ? 'bg-blue-500 text-white border-blue-600 shadow-md' 
                                : 'bg-white hover:bg-blue-50 border-gray-200 hover:border-blue-300'
                            }`}
                          >
                            <div className="flex items-center space-x-2 w-full min-w-0">
                              <div className={`flex-shrink-0 w-4 h-4 rounded-full border flex items-center justify-center ${
                                isSelected 
                                  ? 'bg-white border-white' 
                                  : 'border-gray-300'
                              }`}>
                                {isSelected && <FaCheck className="text-blue-600 text-xs" />}
                              </div>
                              <div className="flex-grow min-w-0">
                                <span className={`text-sm font-medium truncate block ${isSelected ? 'text-white' : 'text-gray-800'}`} title={course}>
                                  {getCompactCourseName(course)}
                                </span>
                              </div>
                            </div>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                const newCourses = e.target.checked 
                                  ? [...formData.allowedCourses, course]
                                  : formData.allowedCourses.filter(c => c !== course);
                                setFormData(prev => ({ ...prev, allowedCourses: newCourses }));
                              }}
                              className="sr-only"
                            />
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800 flex items-center">
                      <FaExclamationTriangle className="mr-2 text-amber-600" />
                      <strong>Importante:</strong> Los cursos seleccionados determinan qué estudiantes puede ver este usuario al iniciar sesión.
                    </p>
                  </div>
                </div>

                {/* DIMENSIONES PERMITIDAS - SOLO PARA PROFESORES */}
                {formData.role === 'teacher' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-lg font-semibold text-gray-800 flex items-center">
                        <FaEye className="mr-2 text-purple-600" />
                        Dimensiones Observables
                        <span className="ml-2 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                          {formData.allowedDimensions?.length || 0} de {dimensions.length}
                        </span>
                      </label>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, allowedDimensions: dimensions.map(d => Number(d.id))})}
                          className="flex items-center space-x-1 text-sm bg-purple-50 text-purple-700 px-3 py-2 rounded-lg hover:bg-purple-100 transition-colors border border-purple-200"
                        >
                          <FaCheck className="text-xs" />
                          <span>Todas</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, allowedDimensions: []})}
                          className="flex items-center space-x-1 text-sm bg-gray-50 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
                        >
                          <FaTimes className="text-xs" />
                          <span>Ninguna</span>
                        </button>
                      </div>
                    </div>
                    
                    <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                        {dimensions.map(dimension => {
                          // Asegurar comparación correcta de tipos (number)
                          const dimId = Number(dimension.id);
                          const isSelected = formData.allowedDimensions.some(id => Number(id) === dimId);
                          const categoryColors: Record<string, string> = {
                            "Exploración de uno mismo": "bg-blue-500",
                            "Exploración del mundo": "bg-green-500", 
                            "Exploración de lo Trascendente": "bg-purple-500",
                            "Descripción Cualitativa": "bg-orange-500"
                          };
                          const categoryColor = categoryColors[dimension.category] || "bg-gray-500";
                          
                          return (
                            <label 
                              key={dimension.id} 
                              className={`group relative flex items-start p-2 rounded-lg cursor-pointer transition-all duration-200 border ${
                                isSelected 
                                  ? 'border-purple-400 bg-purple-100 shadow-sm' 
                                  : 'border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => handleDimensionChange(dimension.id, e.target.checked)}
                                className="sr-only"
                              />
                              <div className="flex items-start space-x-2 w-full">
                                <div className={`flex-shrink-0 w-6 h-6 ${categoryColor} rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5`}>
                                  {dimension.id}
                                </div>
                                <div className="flex-grow min-w-0">
                                  <span className={`text-xs font-medium leading-tight block ${
                                    isSelected ? 'text-purple-900' : 'text-gray-700'
                                  }`}>
                                    {dimension.name}
                                  </span>
                                </div>
                                <div className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-all mt-0.5 ${
                                  isSelected 
                                    ? 'bg-purple-500 border-purple-500' 
                                    : 'border-gray-300 group-hover:border-purple-400'
                                }`}>
                                  {isSelected && <FaCheck className="w-2.5 h-2.5 text-white" />}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm text-gray-600 bg-purple-50 p-3 rounded-lg border border-purple-200">
                      <div className="flex items-center space-x-2">
                        <FaEye className="text-purple-600" />
                        <span>Estas dimensiones definen qué puede evaluar este profesor</span>
                      </div>
                      <div className="text-purple-700 font-medium">
                        {((formData.allowedDimensions?.length || 0) / dimensions.length * 100).toFixed(0)}% seleccionado
                      </div>
                    </div>
                  </div>
                )}

                {/* Settings */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-800">Configuración</h3>
                  
                  <div className="space-y-3">
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Usuario activo</span>
                    </label>
                    
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={formData.mustChangePassword}
                        onChange={(e) => setFormData(prev => ({ ...prev, mustChangePassword: e.target.checked }))}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Debe cambiar contraseña en el próximo login</span>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedUser(null);
                    setModalError(null);
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveUser}
                  disabled={loading}
                  className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {loading && <FaSpinner className="animate-spin" />}
                  <span>{selectedUser ? 'Actualizar' : 'Crear'} Usuario</span>
                </button>
              </div>
            </div>
          </div>
        )}

                {/* Modal Wizard de Nueva Meta */}
        {showGoalsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden">
              {/* Header con Progreso */}
              <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-700 p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                      <FaBullseye className="w-8 h-8" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold">🚀 Crear Nueva Meta</h2>
                      <p className="text-blue-100 font-medium">Sistema de creación paso a paso</p>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseGoalModal}
                    className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition-colors"
                  >
                    <FaTimes className="w-6 h-6" />
                  </button>
                </div>

                {/* Indicador de Pasos */}
                <div className="flex items-center space-x-4">
                  {[1, 2, 3].map((step) => (
                    <div key={step} className="flex items-center">
                      <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 font-bold ${
                        currentStep === step 
                          ? 'bg-white text-purple-600 border-white' 
                          : currentStep > step 
                            ? 'bg-green-500 text-white border-green-500' 
                            : 'bg-white/20 text-white border-white/50'
                      }`}>
                        {currentStep > step ? <FaCheck /> : step}
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium">
                          {step === 1 ? 'Detalles de la Meta' : step === 2 ? 'Seleccionar Usuarios' : 'Vista Previa'}
                        </div>
                      </div>
                      {step < 3 && (
                        <div className={`w-12 h-1 mx-4 rounded-full ${
                          currentStep > step ? 'bg-green-500' : 'bg-white/30'
                        }`}></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="max-h-[calc(95vh-180px)] overflow-y-auto">
                <div className="p-8">
                  {/* Paso 1: Detalles de la Meta */}
                  {currentStep === 1 && (
                    <div className="space-y-6">
                      <div className="text-center mb-8">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">📝 Paso 1: Detalles de la Meta</h3>
                        <p className="text-gray-600">Define los objetivos y características de tu meta</p>
                      </div>

                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-200">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <div className="lg:col-span-2">
                            <label className="block text-sm font-bold text-gray-700 mb-3">
                              🎯 Título de la Meta *
                            </label>
                            <input
                              type="text"
                              value={goalFormData.title}
                              onChange={(e) => setGoalFormData(prev => ({ ...prev, title: e.target.value }))}
                              className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium text-lg"
                              placeholder="Ej: Alcanzar 90% de evaluaciones completadas este mes"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-bold text-gray-700 mb-3">
                              📅 Fecha de Inicio *
                            </label>
                            <input
                              type="date"
                              value={goalFormData.startDate}
                              onChange={(e) => setGoalFormData(prev => ({ ...prev, startDate: e.target.value }))}
                              className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-bold text-gray-700 mb-3">
                              🏁 Fecha Límite *
                            </label>
                            <input
                              type="date"
                              value={goalFormData.targetDate}
                              onChange={(e) => setGoalFormData(prev => ({ ...prev, targetDate: e.target.value }))}
                              min={goalFormData.startDate || new Date().toISOString().split('T')[0]}
                              className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                            />
                          </div>
                          
                          <div className="lg:col-span-2">
                            <label className="block text-sm font-bold text-gray-700 mb-3">
                              ⚡ Prioridad
                            </label>
                            <div className="grid grid-cols-3 gap-4">
                              {[
                                { value: 'low', label: 'Baja Prioridad', icon: '🟢', color: 'border-green-200 bg-green-50' },
                                { value: 'medium', label: 'Media Prioridad', icon: '🟡', color: 'border-yellow-200 bg-yellow-50' },
                                { value: 'high', label: 'Alta Prioridad', icon: '🔴', color: 'border-red-200 bg-red-50' }
                              ].map((priority) => (
                                <label
                                  key={priority.value}
                                  className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                                    goalFormData.priority === priority.value 
                                      ? `${priority.color} border-current ring-4 ring-opacity-20` 
                                      : 'border-gray-200 bg-white hover:border-gray-300'
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name="priority"
                                    value={priority.value}
                                    checked={goalFormData.priority === priority.value}
                                    onChange={(e) => setGoalFormData(prev => ({ ...prev, priority: e.target.value as 'low' | 'medium' | 'high' }))}
                                    className="sr-only"
                                  />
                                  <div className="text-center">
                                    <div className="text-2xl mb-2">{priority.icon}</div>
                                    <div className="font-bold text-sm">{priority.label}</div>
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                          
                          <div className="lg:col-span-2">
                            <label className="block text-sm font-bold text-gray-700 mb-3">
                              📝 Descripción Detallada
                            </label>
                            <textarea
                              value={goalFormData.description}
                              onChange={(e) => setGoalFormData(prev => ({ ...prev, description: e.target.value }))}
                              className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all h-32 resize-none"
                              placeholder="Describe los objetivos específicos, beneficios esperados y criterios de éxito..."
                            />
                          </div>
                        </div>

                        {/* Validación Visual */}
                        {!validateStep1() && goalFormData.title && (
                          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                            <div className="flex items-center space-x-2 text-amber-800">
                              <FaExclamationTriangle className="w-5 h-5" />
                              <span className="font-medium">Campos requeridos:</span>
                            </div>
                            <ul className="mt-2 text-sm text-amber-700 list-disc list-inside">
                              {!goalFormData.title && <li>Título de la meta</li>}
                              {!goalFormData.startDate && <li>Fecha de inicio</li>}
                              {!goalFormData.targetDate && <li>Fecha límite</li>}
                              {goalFormData.startDate && goalFormData.targetDate && new Date(goalFormData.startDate) > new Date(goalFormData.targetDate) && (
                                <li>La fecha de inicio debe ser anterior a la fecha límite</li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Paso 2: Seleccionar Usuarios */}
                  {currentStep === 2 && (
                    <div className="space-y-6">
                      <div className="text-center mb-8">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">👥 Paso 2: Seleccionar Usuarios</h3>
                        <p className="text-gray-600">Elige qué usuarios tendrán esta meta asignada</p>
                      </div>

                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-200">
                        <div className="flex items-center justify-between mb-6">
                          <h4 className="text-lg font-semibold text-gray-900">
                            Usuarios Disponibles ({selectedUsersForGoal.length} seleccionados)
                          </h4>
                          <div className="flex space-x-2">
                            <button
                              onClick={selectAllUsers}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                            >
                              Seleccionar Todos
                            </button>
                            <button
                              onClick={() => {
                                const teacherIds = users
                                  .filter(u => u !== undefined && u !== null && u.role === 'teacher') // 🛡️ Filtrar elementos undefined/null
                                  .map(u => getUserId(u));
                                setSelectedUsersForGoal(teacherIds);
                              }}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                            >
                              Solo Profesores
                            </button>
                            <button
                              onClick={deselectAllUsers}
                              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors text-sm"
                            >
                              Limpiar
                            </button>
                          </div>
                        </div>
                        
                        <div className="grid gap-3 max-h-96 overflow-y-auto">
                          {users.map(user => {
                            const userId = getUserId(user);
                            const isSelected = selectedUsersForGoal.includes(userId);
                            
                            return (
                              <div
                                key={userId}
                                className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                                  isSelected 
                                    ? 'border-green-500 bg-green-50' 
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                }`}
                                onClick={() => toggleUserSelection(userId)}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                      isSelected ? 'bg-green-500 border-green-500' : 'border-gray-300'
                                    }`}>
                                      {isSelected && <FaCheck className="w-3 h-3 text-white" />}
                                    </div>
                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                      {(user.firstName?.[0] || user.username?.[0] || 'U').toUpperCase()}
                                    </div>
                                    <div>
                                      <div className="font-semibold text-gray-900">
                                        {user.firstName} {user.lastName}
                                      </div>
                                      <div className="text-sm text-gray-500">
                                        {user.username} • {user.role === 'admin' ? '👑 Admin' : '👨‍🏫 Profesor'}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-sm text-gray-600">
                                      {user.allowedCourses?.length || 0} cursos
                                    </div>
                                    <div className={`text-xs px-2 py-1 rounded-full ${
                                      user.isActive !== false 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-red-100 text-red-800'
                                    }`}>
                                      {user.isActive !== false ? 'Activo' : 'Inactivo'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Validación Paso 2 */}
                        {selectedUsersForGoal.length === 0 && (
                          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                            <div className="flex items-center space-x-2 text-amber-800">
                              <FaExclamationTriangle className="w-5 h-5" />
                              <span className="font-medium">Debes seleccionar al menos un usuario para continuar</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Paso 3: Vista Previa y Confirmación */}
                  {currentStep === 3 && (
                    <div className="space-y-6">
                      <div className="text-center mb-8">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">👁️ Paso 3: Vista Previa y Confirmación</h3>
                        <p className="text-gray-600">Revisa todos los detalles antes de crear la meta</p>
                      </div>

                      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 border border-purple-200">
                        {/* Resumen de la Meta */}
                        <div className="bg-white rounded-xl p-6 mb-6 border border-gray-200">
                          <h4 className="text-xl font-bold text-gray-900 mb-4">📋 Resumen de la Meta</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="text-sm font-medium text-gray-600">Título</label>
                              <p className="text-lg font-semibold text-gray-900">{goalFormData.title}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-600">Prioridad</label>
                              <div className={`inline-block px-3 py-1 rounded-full text-sm font-bold mt-1 ${
                                goalFormData.priority === 'high' ? 'bg-red-100 text-red-800' :
                                goalFormData.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {goalFormData.priority === 'high' ? '🔴 Alta Prioridad' :
                                 goalFormData.priority === 'medium' ? '🟡 Media Prioridad' : '🟢 Baja Prioridad'}
                              </div>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-600">Fecha de Inicio</label>
                              <p className="text-lg font-semibold text-gray-900">
                                {new Date(goalFormData.startDate).toLocaleDateString('es-ES', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-600">Fecha Límite</label>
                              <p className="text-lg font-semibold text-gray-900">
                                {new Date(goalFormData.targetDate).toLocaleDateString('es-ES', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </p>
                            </div>
                            <div className="md:col-span-2">
                              <label className="text-sm font-medium text-gray-600">Descripción</label>
                              <p className="text-gray-900 mt-1">{goalFormData.description || 'Sin descripción'}</p>
                            </div>
                            <div className="md:col-span-2">
                              <label className="text-sm font-medium text-gray-600">Duración</label>
                              <p className="text-lg font-semibold text-gray-900">
                                {Math.ceil((new Date(goalFormData.targetDate).getTime() - new Date(goalFormData.startDate).getTime()) / (1000 * 60 * 60 * 24))} días
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Usuarios Seleccionados */}
                        <div className="bg-white rounded-xl p-6 border border-gray-200">
                          <h4 className="text-xl font-bold text-gray-900 mb-4">👥 Usuarios Asignados ({selectedUsersForGoal.length})</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {selectedUsersForGoal.map(userId => {
                              const user = users.find(u => getUserId(u) === userId);
                              if (!user) return null;
                              
                              return (
                                <div key={userId} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-xs">
                                    {(user.firstName?.[0] || user.username?.[0] || 'U').toUpperCase()}
                                  </div>
                                  <div>
                                    <div className="font-medium text-gray-900 text-sm">
                                      {user.firstName} {user.lastName}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {user.role === 'admin' ? '👑 Admin' : '👨‍🏫 Profesor'}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Botones de Navegación */}
                  <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                    <button
                      onClick={goToPreviousStep}
                      disabled={currentStep === 1}
                      className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all ${
                        currentStep === 1 
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      <FaArrowRight className="w-4 h-4 transform rotate-180" />
                      <span>Anterior</span>
                    </button>

                    <div className="text-center">
                      <span className="text-sm text-gray-500">Paso {currentStep} de 3</span>
                    </div>

                    {currentStep < 3 ? (
                      <button
                        onClick={goToNextStep}
                        disabled={
                          (currentStep === 1 && !validateStep1()) ||
                          (currentStep === 2 && !validateStep2())
                        }
                        className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all ${
                          ((currentStep === 1 && !validateStep1()) || (currentStep === 2 && !validateStep2()))
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                            : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white'
                        }`}
                      >
                        <span>Siguiente</span>
                        <FaArrowRight className="w-4 h-4" />
                      </button>
                    ) : (
                                             <button
                         onClick={async () => {
                           if (!goalFormData.title || !goalFormData.startDate || !goalFormData.targetDate) {
                             showError('Por favor completa todos los campos requeridos');
                             return;
                           }
                           
                           const goalData = {
                             title: goalFormData.title,
                             description: goalFormData.description,
                             startDate: goalFormData.startDate,
                             targetDate: goalFormData.targetDate,
                             priority: goalFormData.priority
                           };
                           
                           console.log('🎯 Creando meta con datos:', goalData);
                           await createGoalForSelectedUsers(goalData);
                           handleCloseGoalModal();
                         }}
                         disabled={loading || !validateStep1() || !validateStep2()}
                         className="flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-bold transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                       >
                         {loading && <FaSpinner className="animate-spin" />}
                         <FaCheck className="w-4 h-4" />
                         <span>Crear Meta</span>
                       </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Enhanced Users View with modern cards
  function renderUsersView() {
    const currentUser = UserService.getCurrentUser();
    const isTeacher = currentUser?.role === 'teacher';
    
    // Si es profesor, mostrar solo su perfil
    if (isTeacher) {
      const userProfile = users.find(u => u.username === currentUser?.username);
      
      if (!userProfile) {
        return (
          <div className="p-6 text-center">
            <FaExclamationTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Perfil no encontrado</h3>
            <p className="text-gray-600">No se pudo cargar tu información de perfil.</p>
          </div>
        );
      }

      return (
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Mi Perfil</h2>
              <p className="text-gray-600 mt-1">Información personal y configuración de cuenta</p>
            </div>
          </div>

          {/* User Profile Card */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6 text-white">
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <span className="text-2xl font-bold">
                    {userProfile.firstName?.[0] || userProfile.username?.[0] || 'U'}
                  </span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold">{userProfile.firstName} {userProfile.lastName}</h3>
                  <p className="text-blue-100">{userProfile.username}</p>
                  <div className="flex items-center space-x-2 mt-2">
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                      👨‍🏫 Profesor
                    </span>
                    <span className={`w-3 h-3 rounded-full ${userProfile.isActive ? 'bg-green-300' : 'bg-red-300'}`}></span>
                    <span className="text-sm">{userProfile.isActive ? 'Activo' : 'Inactivo'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Información Básica */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Información Personal</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Email</label>
                      <p className="text-gray-900">{userProfile.email || 'No especificado'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Último Acceso</label>
                      <p className="text-gray-900">
                        {userProfile.lastLogin ? new Date(userProfile.lastLogin).toLocaleString() : 'Nunca'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Cursos Asignados */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Cursos Asignados</h4>
                  {userProfile.allowedCourses && userProfile.allowedCourses.length > 0 ? (
                    <div className="space-y-2">
                      {userProfile.allowedCourses.map((course, index) => (
                        <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="flex items-center space-x-2">
                            <FaGraduationCap className="text-blue-600" />
                            <span className="font-medium text-blue-900">{course}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">No hay cursos asignados</p>
                  )}
                </div>
              </div>

              {/* Metas del Usuario */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">Mis Metas ({userProfile.goals?.length || 0})</h4>
                  <button
                    onClick={() => setCurrentView('goals')}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center space-x-1"
                  >
                    <span>Ver todas</span>
                    <FaArrowRight className="w-3 h-3" />
                  </button>
                </div>
                
                {userProfile.goals && userProfile.goals.length > 0 ? (
                  <div className="grid gap-3">
                    {userProfile.goals.slice(0, 3).map(goal => (
                      <div key={goal.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h5 className="font-medium text-gray-900">{goal.title}</h5>
                            <p className="text-sm text-gray-600 mt-1">{goal.description}</p>
                            <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                              <span>Vence: {new Date(goal.targetDate).toLocaleDateString()}</span>
                              <span className={`px-2 py-1 rounded-full ${
                                goal.status === 'completed' ? 'bg-green-100 text-green-800' :
                                goal.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                goal.status === 'overdue' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {goal.status === 'completed' ? 'Completada' :
                                 goal.status === 'in_progress' ? 'En Progreso' :
                                 goal.status === 'overdue' ? 'Vencida' : 'Pendiente'}
                              </span>
                            </div>
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-lg font-bold text-gray-900">{goal.progress}%</div>
                            <div className="w-16 bg-gray-200 rounded-full h-2 mt-1">
                              <div 
                                className={`h-2 rounded-full ${
                                  goal.progress === 100 ? 'bg-green-500' :
                                  goal.progress >= 75 ? 'bg-blue-500' :
                                  goal.progress >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${goal.progress}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FaBullseye className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No tienes metas asignadas</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Vista completa para administradores
    return (
      <div className="p-6">
        {/* KPIs - Estadísticas Rápidas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-blue-100 text-sm font-medium">Total Usuarios</div>
                <div className="text-3xl font-bold">{getUserStats().totalUsers}</div>
              </div>
              <FaUsers className="text-4xl text-blue-200" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-green-100 text-sm font-medium">Activos</div>
                <div className="text-3xl font-bold">{getUserStats().activeUsers}</div>
              </div>
              <FaEye className="text-4xl text-green-200" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-purple-100 text-sm font-medium">Administradores</div>
                <div className="text-3xl font-bold">{getUserStats().adminUsers}</div>
              </div>
              <FaCrown className="text-4xl text-purple-200" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-yellow-100 text-sm font-medium">Profesores</div>
                <div className="text-3xl font-bold">{getUserStats().teacherUsers}</div>
              </div>
              <FaUserTie className="text-4xl text-yellow-200" />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Lista de Usuarios</h2>
          <div className="flex space-x-2">
            {/* View Mode Toggle */}
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setUsersViewMode('list')}
                className={`px-3 py-2 flex items-center space-x-1 text-sm transition-colors ${
                  usersViewMode === 'list' 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                title="Vista de lista"
              >
                <FaList />
                <span>Lista</span>
              </button>
              <button
                onClick={() => setUsersViewMode('cards')}
                className={`px-3 py-2 flex items-center space-x-1 text-sm transition-colors ${
                  usersViewMode === 'cards' 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                title="Vista de tarjetas"
              >
                <FaTh />
                <span>Tarjetas</span>
              </button>
            </div>
            <button
              onClick={handleCreateUser}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2"
            >
              <FaPlus />
              <span>Nuevo Usuario</span>
            </button>
          </div>
        </div>

        {/* Enhanced Filters */}
        <div className="bg-gray-50 p-4 rounded-lg space-y-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center space-x-2">
              <FaSearch className="text-gray-500" />
              <input
                type="text"
                placeholder="Buscar por nombre, email o usuario..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-64"
              />
            </div>
            
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">🎭 Todos los roles</option>
              <option value="admin">👑 Administradores</option>
              <option value="teacher">👨‍🏫 Profesores</option>
            </select>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">📊 Todos los estados</option>
              <option value="active">✅ Activos</option>
              <option value="inactive">❌ Inactivos</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="name">📝 Ordenar por nombre</option>
              <option value="role">🎭 Ordenar por rol</option>
              <option value="lastLogin">🕐 Último acceso</option>
              <option value="created">📅 Fecha creación</option>
            </select>
            
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              title={`Orden ${sortOrder === 'asc' ? 'ascendente' : 'descendente'}`}
            >
              <FaSort className={sortOrder === 'desc' ? 'transform rotate-180' : ''} />
            </button>
          </div>
        </div>

        {/* List View */}
        {usersViewMode === 'list' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Usuario</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Rol</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Estado</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Cursos</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-gray-700">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map((user) => (
                  <tr key={getUserId(user)} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                          user.role === 'admin' ? 'bg-purple-500' : 'bg-blue-500'
                        }`}>
                          {user.firstName ? user.firstName[0] : (user.username || 'U')[0]}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {user.firstName && user.lastName 
                              ? `${user.firstName} ${user.lastName}` 
                              : (user.username || 'Usuario')}
                          </div>
                          <div className="text-sm text-gray-500">{user.email || 'Sin email'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        user.role === 'admin' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {user.role === 'admin' ? '👑 Admin' : '👨‍🏫 Profesor'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <span className={`w-3 h-3 rounded-full ${user.isActive ? 'bg-green-500' : 'bg-red-500'}`}></span>
                        <span className={`text-sm font-medium ${user.isActive ? 'text-green-700' : 'text-red-700'}`}>
                          {user.isActive ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                      {user.passwordResetRequestedAt && (
                        <span className="mt-1 inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800" title={`Solicitado el ${new Date(user.passwordResetRequestedAt).toLocaleString('es-CL')}`}>
                          🔑 Pidió recuperar clave
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">{user.allowedCourses?.length || 0} cursos</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="p-3 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 rounded-lg transition-colors"
                          title="Editar usuario"
                        >
                          <FaEdit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleResetPassword(getUserId(user))}
                          className={`p-3 rounded-lg transition-colors ${user.passwordResetRequestedAt ? 'bg-amber-200 text-amber-900 hover:bg-amber-300' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}
                          title="Generar clave temporal"
                        >
                          <FaKey className="w-5 h-5" />
                        </button>
                        {getUserId(user) !== '1' && (
                          <button
                            onClick={() => handleDeleteUser(getUserId(user))}
                            className="p-3 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors"
                            title="Eliminar usuario"
                          >
                            <FaTrash className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Cards View */}
        {usersViewMode === 'cards' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredUsers.map((user) => (
              <div 
                key={getUserId(user)} 
                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
              >
                {/* Card Header */}
                <div className={`p-4 ${
                  user.role === 'admin' 
                    ? 'bg-gradient-to-r from-purple-500 to-indigo-600' 
                    : 'bg-gradient-to-r from-blue-500 to-blue-600'
                }`}>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {user.firstName ? user.firstName[0] : (user.username || 'U')[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-semibold truncate">
                        {user.firstName && user.lastName 
                          ? `${user.firstName} ${user.lastName}` 
                          : (user.username || 'Usuario')}
                      </h3>
                      <p className="text-white/80 text-sm truncate">{user.email || 'Sin email'}</p>
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-4">
                  <div className="space-y-3">
                    {/* Status and Role */}
                    <div className="flex items-center justify-between">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        user.role === 'admin' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {user.role === 'admin' ? '👑 Admin' : '👨‍🏫 Profesor'}
                      </span>
                      
                      <div className="flex items-center space-x-2">
                        <span className={`w-3 h-3 rounded-full ${user.isActive ? 'bg-green-500' : 'bg-red-500'}`}></span>
                        <span className={`text-sm font-medium ${user.isActive ? 'text-green-700' : 'text-red-700'}`}>
                          {user.isActive ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="text-sm text-gray-600">
                      <span>{user.allowedCourses?.length || 0} cursos asignados</span>
                    </div>
                    {user.passwordResetRequestedAt && (
                      <button
                        onClick={() => handleResetPassword(getUserId(user))}
                        className="w-full px-3 py-2 text-xs font-semibold rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200"
                      >
                        🔑 Pidió recuperar clave · Generar clave temporal
                      </button>
                    )}

                    {/* Action Buttons - Más grandes y solo Editar/Eliminar */}
                    <div className="flex justify-center gap-3 pt-3 border-t border-gray-200">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="flex-1 flex items-center justify-center space-x-2 py-3 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 rounded-lg transition-colors font-medium"
                        title="Editar usuario"
                      >
                        <FaEdit className="w-5 h-5" />
                        <span>Editar</span>
                      </button>
                      {getUserId(user) !== '1' && (
                        <button
                          onClick={() => handleDeleteUser(getUserId(user))}
                          className="flex-1 flex items-center justify-center space-x-2 py-3 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors font-medium"
                          title="Eliminar usuario"
                        >
                          <FaTrash className="w-5 h-5" />
                          <span>Eliminar</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <FaUsers className="mx-auto text-gray-400 text-6xl mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No se encontraron usuarios</h3>
            <p className="text-gray-500 mb-6">Intenta ajustar los filtros o crear un nuevo usuario</p>
            <button
              onClick={handleCreateUser}
              className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 flex items-center space-x-2 mx-auto"
            >
              <FaPlus />
              <span>Crear Primer Usuario</span>
            </button>
          </div>
        )}
      </div>
    );
  }





  // Goals Management View
  function renderGoalsView() {
    // KPIs mejorados basados en datos reales del sistema
    // Usar los KPIs del componente principal



    const priorityConfig = {
      low: { color: 'text-green-600 bg-green-50', label: 'Baja' },
      medium: { color: 'text-yellow-600 bg-yellow-50', label: 'Media' },
      high: { color: 'text-red-600 bg-red-50', label: 'Alta' }
    };

    const statusConfig = {
      pending: { color: 'text-gray-600 bg-gray-50', label: 'Pendiente' },
      in_progress: { color: 'text-blue-600 bg-blue-50', label: 'En Progreso' },
      completed: { color: 'text-green-600 bg-green-50', label: 'Completada' },
      overdue: { color: 'text-red-600 bg-red-50', label: 'Vencida' }
    };

    const getUserGoals = (userId: string) => {
      const user = users.find(u => getUserId(u) === userId);
      return user?.goals || [];
    };

    const getGoalsByCategory = (category: string) => {
      return users.flatMap(user => 
        (user.goals || []).filter(goal => 
          goal.title.toLowerCase().includes(category.toLowerCase()) ||
          goal.description.toLowerCase().includes(category.toLowerCase())
        )
      );
    };

    return (
      <div className="space-y-6">
        {/* Header Mejorado con Animaciones */}
        <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-700 rounded-xl shadow-lg p-6 text-white overflow-hidden">
          {/* Elementos decorativos animados */}
          <div className="absolute top-0 left-0 w-full h-full">
            <div className="absolute top-4 right-4 w-20 h-20 bg-white/10 rounded-full animate-pulse"></div>
            <div className="absolute bottom-4 left-4 w-16 h-16 bg-white/5 rounded-full animate-bounce"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-white/5 rounded-full"></div>
          </div>
          
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative bg-white/20 p-4 rounded-xl backdrop-blur-sm border border-white/30">
                <FaBullseye className="w-8 h-8" />
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
                </div>
              </div>
              <div>
                <h2 className="text-3xl font-bold bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                  🎯 Gestión de Metas
                </h2>
                <p className="text-blue-100 font-medium">Sistema de gestión y seguimiento de objetivos</p>
                <div className="flex items-center space-x-4 mt-2">
                  <div className="flex items-center space-x-1 text-sm">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-blue-100">Sistema activo</span>
                  </div>
                  <div className="flex items-center space-x-1 text-sm">
                    <FaChartLine className="w-3 h-3 text-blue-200" />
                    <span className="text-blue-100">Seguimiento de progreso</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-right mr-4">
                <div className="text-2xl font-bold">{users.filter(u => u.goals && u.goals.length > 0).length}</div>
                <div className="text-blue-100 text-sm">usuarios con metas</div>
              </div>
              {/* Solo mostrar botones de administración para admins */}
              {UserService.getCurrentUser()?.role === 'admin' && (
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => {
                      setSelectedUser(null); // Para meta global
                      setShowGoalsModal(true);
                    }}
                    className="bg-white/20 hover:bg-white/30 px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-lg flex items-center space-x-2 font-bold backdrop-blur-sm border border-white/30 group"
                  >
                    <FaBullseye className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
                    <span>Nueva Meta</span>
                    <div className="ml-2 px-2 py-1 bg-white/20 rounded-full text-xs group-hover:bg-white/30 transition-colors">
                      Personalizada
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setShowBulkDeleteModal(true)}
                    className="bg-red-600/90 hover:bg-red-700 px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-lg flex items-center space-x-2 font-bold backdrop-blur-sm border border-red-500/30 group text-white"
                  >
                    <FaTrash className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                    <span>Eliminar Metas</span>
                    <div className="ml-2 px-2 py-1 bg-white/20 rounded-full text-xs group-hover:bg-white/30 transition-colors">
                      Masivo
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* KPIs del Sistema Mejorados */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* KPI 1: Progreso de Ingreso de Datos */}
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-blue-50">
                <FaChartLine className="w-6 h-6 text-blue-600" />
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">{kpis.dataInputProgress.current}%</div>
                <div className="text-sm text-gray-500">de {kpis.dataInputProgress.target}%</div>
              </div>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{kpis.dataInputProgress.label}</h3>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-600">Esperado: {kpis.dataInputProgress.expected}%</span>
              <span className={`font-medium ${kpis.dataInputProgress.current >= kpis.dataInputProgress.expected ? 'text-green-600' : 'text-red-600'}`}>
                {kpis.dataInputProgress.current >= kpis.dataInputProgress.expected ? '✓ Al día' : '⚠ Atrasado'}
              </span>
            </div>
            <div className="bg-gray-200 rounded-full h-3 mb-2">
              <div 
                className={`h-3 rounded-full transition-all duration-500 ${
                  kpis.dataInputProgress.current >= kpis.dataInputProgress.target ? 'bg-green-500' : 
                  kpis.dataInputProgress.current >= kpis.dataInputProgress.expected ? 'bg-blue-500' : 'bg-red-500'
                }`}
                style={{ width: `${Math.min(kpis.dataInputProgress.current / kpis.dataInputProgress.target * 100, 100)}%` }}
              ></div>
            </div>
          </div>

          {/* KPI 2: Evaluaciones Completadas */}
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-green-50">
                <FaBookOpen className="w-6 h-6 text-green-600" />
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">{kpis.skillAssessmentCompletion.current}%</div>
                <div className="text-sm text-green-600 font-medium">{kpis.skillAssessmentCompletion.trend}</div>
              </div>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{kpis.skillAssessmentCompletion.label}</h3>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-600">Meta: {kpis.skillAssessmentCompletion.target}%</span>
              <span className="text-gray-500">307 de 420 estudiantes</span>
            </div>
            <div className="bg-gray-200 rounded-full h-3">
              <div 
                className="bg-green-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${kpis.skillAssessmentCompletion.current / kpis.skillAssessmentCompletion.target * 100}%` }}
              ></div>
            </div>
          </div>

          {/* KPI 3: Participación de Usuarios */}
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-purple-50">
                <FaUsers className="w-6 h-6 text-purple-600" />
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">{kpis.userEngagement.current}%</div>
                <div className="text-sm text-gray-500">{kpis.userEngagement.active}/{kpis.userEngagement.total}</div>
              </div>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{kpis.userEngagement.label}</h3>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-600">Meta: {kpis.userEngagement.target}%</span>
              <span className={`font-medium ${kpis.userEngagement.current >= kpis.userEngagement.target ? 'text-green-600' : 'text-yellow-600'}`}>
                {kpis.userEngagement.current >= kpis.userEngagement.target ? 'Excelente' : 'Mejorable'}
              </span>
            </div>
            <div className="bg-gray-200 rounded-full h-3">
              <div 
                className="bg-purple-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${kpis.userEngagement.current}%` }}
              ></div>
            </div>
          </div>

          {/* KPI 4: Calidad de Datos */}
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-orange-500">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-orange-50">
                <FaRocket className="w-6 h-6 text-orange-600" />
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">{kpis.dataQuality.current}%</div>
                <div className="text-sm text-gray-500">de calidad</div>
              </div>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{kpis.dataQuality.label}</h3>
            <div className="flex items-center justify-between text-sm mb-2">
                                <span className="text-gray-600">{kpis.dataQuality.complete} estudiantes completos</span>
                  <span className="text-gray-500">de {kpis.dataQuality.total}</span>
            </div>
            <div className="bg-gray-200 rounded-full h-3">
              <div 
                className="bg-orange-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${kpis.dataQuality.current}%` }}
              ></div>
            </div>
          </div>
        </div>





        {/* Users with Goals */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Metas por Usuario</h3>
          {/* Debug info */}
          <div className="mb-4 p-3 bg-gray-100 rounded-lg text-sm">
            <strong>Debug Info:</strong> {users.length} usuarios cargados, {users.filter(u => u.goals && u.goals.length > 0).length} con metas
          </div>
          <div className="space-y-4">
            {(UserService.getCurrentUser()?.role === 'teacher' 
              ? users.filter(u => u.username === UserService.getCurrentUser()?.username)
              : users
            ).map(user => {
              const userGoals = user.goals || [];
              const completedGoals = userGoals.filter(g => g.status === 'completed').length;
              const isExpanded = expandedCard === getUserId(user);
              
              return (
                <div key={getUserId(user)} className="border border-gray-200 rounded-lg">
                  <div 
                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedCard(isExpanded ? null : getUserId(user))}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                          {(user.firstName?.[0] || user.username?.[0] || 'U').toUpperCase()}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">
                            {user.firstName} {user.lastName} ({user.username})
                          </div>
                          <div className="text-sm text-gray-500">
                            {userGoals.length} metas • {completedGoals} completadas
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900">
                            {userGoals.length > 0 ? Math.round((completedGoals / userGoals.length) * 100) : 0}% completado
                          </div>
                          <div className="w-24 bg-gray-200 rounded-full h-2 mt-1">
                            <div 
                              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                              style={{ 
                                width: `${userGoals.length > 0 ? (completedGoals / userGoals.length) * 100 : 0}%` 
                              }}
                            ></div>
                          </div>
                        </div>
                        {/* Solo mostrar botones de gestión para admins */}
                        {UserService.getCurrentUser()?.role === 'admin' && (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleManageGoals(user);
                              }}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                            >
                              <FaPlus className="w-4 h-4" />
                              <span>Asignar</span>
                            </button>
                            {userGoals.length > 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteAllUserGoals(user);
                                }}
                                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                                title={`Eliminar todas las ${userGoals.length} metas`}
                              >
                                <FaTrash className="w-4 h-4" />
                                <span>Limpiar Todas</span>
                              </button>
                            )}
                          </div>
                        )}
                        {isExpanded ? <FaChevronDown className="w-4 h-4 text-gray-400" /> : <FaChevronRight className="w-4 h-4 text-gray-400" />}
                      </div>
                    </div>
                  </div>
                  
                  {isExpanded && userGoals.length > 0 && (
                    <div className="border-t border-gray-200 p-4 bg-gray-50">
                      <div className="grid gap-4">
                        {userGoals.map(goal => (
                          <div key={goal.id} className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-md transition-all duration-200">
                            {/* Header de la meta con acciones */}
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3 mb-2">
                                  <h4 className="font-bold text-gray-900 text-lg">{goal.title}</h4>
                                  <div className="flex items-center space-x-2">
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${priorityConfig[goal.priority].color}`}>
                                      {priorityConfig[goal.priority].label}
                                    </span>
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${statusConfig[goal.status].color}`}>
                                      {statusConfig[goal.status].label}
                                    </span>
                                  </div>
                                </div>
                                <p className="text-gray-600 mb-3 leading-relaxed">{goal.description}</p>
                                <div className="flex items-center space-x-6 text-sm text-gray-500">
                                  {goal.startDate && (
                                    <span className="flex items-center space-x-1">
                                      <FaCalendarAlt className="w-3 h-3" />
                                      <span>Inicio: {new Date(goal.startDate).toLocaleDateString()}</span>
                                    </span>
                                  )}
                                  <span className="flex items-center space-x-1">
                                    <FaCalendarAlt className="w-3 h-3" />
                                    <span>Vence: {new Date(goal.targetDate).toLocaleDateString()}</span>
                                  </span>
                                  <span className="text-xs">
                                    Asignada por: {goal.assignedBy}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Progreso visual */}
                              <div className="ml-6 text-center">
                                <div className="text-2xl font-bold text-gray-900 mb-1">{goal.progress}%</div>
                                <div className="w-20 bg-gray-200 rounded-full h-3">
                                  <div 
                                    className={`h-3 rounded-full transition-all duration-500 ${
                                      goal.progress === 100 ? 'bg-green-500' : 
                                      goal.progress >= 75 ? 'bg-blue-500' :
                                      goal.progress >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                    }`}
                                    style={{ width: `${goal.progress}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs text-gray-500 mt-1 block">Completado</span>
                              </div>
                            </div>

                            {/* Controles de progreso - Solo para profesores en sus propias metas */}
                            {UserService.getCurrentUser()?.role === 'teacher' && (
                              <div className="bg-blue-50 rounded-lg p-4 mb-4 border border-blue-200">
                                <label className="block text-sm font-medium text-blue-700 mb-2">
                                  Actualizar mi progreso: {goal.progress}%
                                </label>
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  value={goal.progress}
                                  onChange={(e) => handleUpdateGoalProgress(goal.id, parseInt(e.target.value))}
                                  className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer slider"
                                  style={{
                                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${goal.progress}%, #dbeafe ${goal.progress}%, #dbeafe 100%)`
                                  }}
                                />
                                <div className="flex justify-between text-xs text-blue-600 mt-1">
                                  <span>0%</span>
                                  <span>25%</span>
                                  <span>50%</span>
                                  <span>75%</span>
                                  <span>100%</span>
                                </div>
                                <div className="mt-3 flex items-center justify-between">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-sm font-medium text-blue-700">Mi estado:</span>
                                    <select
                                      value={goal.status}
                                      onChange={(e) => handleQuickStatusChange(goal.id, e.target.value, user, goal.title)}
                                      className="text-sm border border-blue-300 rounded-lg px-2 py-1 focus:ring-2 focus:ring-blue-500 bg-white"
                                    >
                                      <option value="pending">⏳ Pendiente</option>
                                      <option value="in_progress">🔄 En Progreso</option>
                                      <option value="completed">✅ Completada</option>
                                    </select>
                                  </div>
                                  <button
                                    onClick={() => {
                                      if (goal.progress === 100) {
                                        handleQuickStatusChange(goal.id, 'completed', user, goal.title);
                                      } else {
                                        handleUpdateGoalProgress(goal.id, 100);
                                        setTimeout(() => handleQuickStatusChange(goal.id, 'completed', user, goal.title), 500);
                                      }
                                    }}
                                    disabled={goal.status === 'completed'}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2 ${
                                      goal.status === 'completed' 
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                        : 'bg-green-500 hover:bg-green-600 text-white'
                                    }`}
                                  >
                                    <FaCheck className="w-3 h-3" />
                                    <span>{goal.status === 'completed' ? 'Completada' : 'Marcar Completada'}</span>
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Controles de administración - Solo para admins */}
                            {UserService.getCurrentUser()?.role === 'admin' && (
                              <>
                                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Ajustar progreso: {goal.progress}%
                                  </label>
                                  <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={goal.progress}
                                    onChange={(e) => handleUpdateGoalProgress(goal.id, parseInt(e.target.value))}
                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                                    style={{
                                      background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${goal.progress}%, #e5e7eb ${goal.progress}%, #e5e7eb 100%)`
                                    }}
                                  />
                                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                                    <span>0%</span>
                                    <span>25%</span>
                                    <span>50%</span>
                                    <span>75%</span>
                                    <span>100%</span>
                                  </div>
                                </div>

                                {/* Botones de acción */}
                                <div className="flex items-center justify-between">
                                  {/* Cambio rápido de estado */}
                                  <div className="flex items-center space-x-2">
                                    <span className="text-sm font-medium text-gray-700">Estado:</span>
                                    <select
                                      value={goal.status}
                                      onChange={(e) => handleQuickStatusChange(goal.id, e.target.value, user, goal.title)}
                                      className="text-sm border border-gray-300 rounded-lg px-2 py-1 focus:ring-2 focus:ring-blue-500"
                                    >
                                      <option value="pending">⏳ Pendiente</option>
                                      <option value="in_progress">🔄 En Progreso</option>
                                      <option value="completed">✅ Completada</option>
                                      <option value="overdue">⚠️ Vencida</option>
                                    </select>
                                  </div>

                                  {/* Botones de gestión */}
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={() => handleEditGoal(goal, user)}
                                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
                                    >
                                      <FaEdit className="w-3 h-3" />
                                      <span>Editar</span>
                                    </button>
                                    <button
                                      onClick={() => {
                                        if (goal.progress === 100) {
                                          handleQuickStatusChange(goal.id, 'completed', user, goal.title);
                                        } else {
                                          handleUpdateGoalProgress(goal.id, 100);
                                          setTimeout(() => handleQuickStatusChange(goal.id, 'completed', user, goal.title), 500);
                                        }
                                      }}
                                      disabled={goal.status === 'completed'}
                                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2 ${
                                        goal.status === 'completed' 
                                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                          : 'bg-green-500 hover:bg-green-600 text-white'
                                      }`}
                                    >
                                      <FaCheck className="w-3 h-3" />
                                      <span>{goal.status === 'completed' ? 'Completada' : 'Completar'}</span>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteGoal(goal.id, goal.title, user)}
                                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
                                    >
                                      <FaTrash className="w-3 h-3" />
                                      <span>Eliminar</span>
                                    </button>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {isExpanded && userGoals.length === 0 && (
                    <div className="border-t border-gray-200 p-4 bg-gray-50 text-center">
                      <FaBullseye className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500">No hay metas asignadas</p>
                      <button
                        onClick={() => handleManageGoals(user)}
                        className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        Asignar primera meta
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }



  // Import Users View
  function renderImportView() {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <FaUpload className="mx-auto text-green-400 text-6xl mb-4" />
          <h3 className="text-2xl font-semibold text-gray-800 mb-2">Importación Masiva</h3>
          <p className="text-gray-600 mb-6">Carga usuarios desde archivos Excel con validación automática</p>
          <div className="max-w-md mx-auto text-left space-y-2 text-gray-600">
            <div className="flex items-center space-x-2">
              <FaCheck className="text-green-500" />
              <span>Soporte para archivos Excel (.xlsx, .csv)</span>
            </div>
            <div className="flex items-center space-x-2">
              <FaCheck className="text-green-500" />
              <span>Validación automática de datos</span>
            </div>
            <div className="flex items-center space-x-2">
              <FaCheck className="text-green-500" />
              <span>Progreso en tiempo real</span>
            </div>
            <div className="flex items-center space-x-2">
              <FaCheck className="text-green-500" />
              <span>Reporte de errores detallado</span>
            </div>
          </div>
          <button
            onClick={() => setShowImportModal(true)}
            className="mt-6 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 flex items-center space-x-2 mx-auto"
          >
            <FaUpload />
            <span>Iniciar Importación</span>
          </button>
        </div>
      </div>
    );
  }


  /* ============================================
     ELIMINADA: Función duplicada de renderAnnualUpdateView
     La función activa está en la línea 2344
     ============================================
  const renderAnnualUpdateView = () => {
    ... código eliminado para evitar duplicación ...
  };
  */

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
        {renderNavigationTabs()}

        <div className="p-6">
          {/* ELIMINADO: Vista General */}
          {/* {currentView === 'overview' && renderOverviewDashboard()} */}
          {currentView === 'users' && renderUsersView()}
          {currentView === 'estudiantes' && (
            <Retiro
              students={propStudents}
              onSectionChange={(section) => {
                // Esta función se puede usar para navegar entre secciones si es necesario
                console.log('Navegando a sección:', section);
              }}
            />
          )}
          
          {currentView === 'informes-retiros' && (
            <InformeNotasRetiros
              students={propStudents}
              onSectionChange={(section) => {
                console.log('Navegando a sección:', section);
              }}
            />
          )}
          {/* OCULTO: Sección de Metas */}
          {/* {currentView === 'goals' && renderGoalsView()} */}
          {/* ELIMINADO: Configurar Skill Levels */}
          {/* {currentView === 'skill-levels' && renderSkillLevelsView()} */}
          {/* Sección Importar eliminada */}
          {/* Sección Backup eliminada */}
          {currentView === 'annual-update' && renderAnnualUpdateView()}
          {currentView === 'advanced-goals' && (
            <AdvancedGoalManager 
              courses={courses}
              userRole="admin"
              userId="admin-user"
              userName="Administrador"
            />
          )}
          {currentView === 'teachers-reports' && (
            <TeacherReportSettings />
          )}

          {/* Sección Importar eliminada */}

        </div>
      </div>

      {/* Quick Goals Modal */}
      {showQuickGoalsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto mx-4">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Metas Rápidas</h2>
                <p className="text-gray-600">Selecciona metas predefinidas para implementar rápidamente</p>
              </div>
              <button
                onClick={() => setShowQuickGoalsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Meta de Fin de Mes - Ingreso de Datos */}
              <div className="border-2 border-blue-200 rounded-xl p-6 hover:border-blue-400 transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <FaCalendarAlt className="w-6 h-6 text-blue-600" />
                  </div>
                  <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                    Alta Prioridad
                  </span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Meta Fin de Mes - Ingreso de Datos</h3>
                <p className="text-gray-600 mb-4">
                  Establecer objetivo de completar {kpis.dataInputProgress.target}% del ingreso de datos antes del fin de mes actual.
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Progreso actual:</span>
                    <span className="font-medium">{kpis.dataInputProgress.current}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Meta objetivo:</span>
                    <span className="font-medium">{kpis.dataInputProgress.target}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Días restantes:</span>
                    <span className="font-medium">
                      {new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate() - new Date().getDate()} días
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    createQuickGoal('monthly-data-target');
                    setShowQuickGoalsModal(false);
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg transition-colors font-medium"
                >
                  Crear Meta de Fin de Mes
                </button>
              </div>

              {/* Meta de Evaluaciones Completas */}
              <div className="border-2 border-green-200 rounded-xl p-6 hover:border-green-400 transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <FaBookOpen className="w-6 h-6 text-green-600" />
                  </div>
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                    Media Prioridad
                  </span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Completar Evaluaciones Pendientes</h3>
                <p className="text-gray-600 mb-4">
                  Objetivo de completar evaluaciones de habilidades para todos los estudiantes del sistema.
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Estudiantes evaluados:</span>
                    <span className="font-medium">307 de 420</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Porcentaje actual:</span>
                    <span className="font-medium">{kpis.skillAssessmentCompletion.current}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Meta objetivo:</span>
                    <span className="font-medium">{kpis.skillAssessmentCompletion.target}%</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setGoalFormData({
                      title: 'Completar evaluaciones de habilidades pendientes',
                      description: `Evaluar a los ${420 - 307} estudiantes restantes para alcanzar ${kpis.skillAssessmentCompletion.target}% de cobertura en evaluaciones.`,
                      startDate: new Date().toISOString().split('T')[0],
                      targetDate: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                      priority: 'medium' as 'low' | 'medium' | 'high'
                    });
                    setShowGoalsModal(true);
                    setShowQuickGoalsModal(false);
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg transition-colors font-medium"
                >
                  Crear Meta de Evaluaciones
                </button>
              </div>

              {/* Meta de Activación de Usuarios */}
              <div className="border-2 border-purple-200 rounded-xl p-6 hover:border-purple-400 transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <FaUsers className="w-6 h-6 text-purple-600" />
                  </div>
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                    Media Prioridad
                  </span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Activar Usuarios Inactivos</h3>
                <p className="text-gray-600 mb-4">
                  Contactar y activar usuarios que no han utilizado el sistema recientemente.
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Usuarios activos:</span>
                    <span className="font-medium">{kpis.userEngagement.active}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total usuarios:</span>
                    <span className="font-medium">{kpis.userEngagement.total}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Participación actual:</span>
                    <span className="font-medium">{kpis.userEngagement.current}%</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setGoalFormData({
                      title: 'Activar usuarios inactivos del sistema',
                      description: `Contactar y reactivar a ${kpis.userEngagement.total - kpis.userEngagement.active} usuarios inactivos para alcanzar ${kpis.userEngagement.target}% de participación.`,
                      startDate: new Date().toISOString().split('T')[0],
                      targetDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                      priority: 'medium' as 'low' | 'medium' | 'high'
                    });
                    setShowGoalsModal(true);
                    setShowQuickGoalsModal(false);
                  }}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg transition-colors font-medium"
                >
                  Crear Meta de Activación
                </button>
              </div>

              {/* Meta de Consistencia de Datos */}
              <div className="border-2 border-orange-200 rounded-xl p-6 hover:border-orange-400 transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <FaRocket className="w-6 h-6 text-orange-600" />
                  </div>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                    Baja Prioridad
                  </span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Mejorar Calidad de Datos</h3>
                <p className="text-gray-600 mb-4">
                  Revisar y completar información faltante en los perfiles de estudiantes.
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Perfiles completos:</span>
                    <span className="font-medium">{kpis.dataQuality.complete}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Calidad actual:</span>
                    <span className="font-medium">{kpis.dataQuality.current}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Meta objetivo:</span>
                    <span className="font-medium">{kpis.dataQuality.target}%</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setGoalFormData({
                      title: 'Mejorar calidad y completitud de datos',
                      description: `Revisar y completar información de ${kpis.dataQuality.total - kpis.dataQuality.complete} estudiantes para alcanzar ${kpis.dataQuality.target}% de calidad de datos.`,
                      startDate: new Date().toISOString().split('T')[0],
                      targetDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                      priority: 'low' as 'low' | 'medium' | 'high'
                    });
                    setShowGoalsModal(true);
                    setShowQuickGoalsModal(false);
                  }}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 rounded-lg transition-colors font-medium"
                >
                  Crear Meta de Calidad
                </button>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  Las metas se asignarán a todos los usuarios relevantes automáticamente
                </p>
                <button
                  onClick={() => setShowQuickGoalsModal(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Modal de Selección de Usuarios */}
      {showUserSelectionModal && currentGoalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
            
            {/* Overlay de Progreso */}
            {creationProgress && (
              <div className="absolute inset-0 bg-white bg-opacity-95 z-10 flex items-center justify-center rounded-xl">
                <div className="text-center p-8">
                  <div className="mb-6">
                    <FaSpinner className="mx-auto text-blue-600 text-4xl animate-spin mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Creando Metas</h3>
                    <p className="text-gray-600">
                      Procesando {creationProgress?.currentUser || 'usuarios'}...
                    </p>
                  </div>
                  
                  <div className="w-full max-w-md mx-auto">
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                      <span>Progreso</span>
                      <span>{creationProgress?.completed || 0} de {creationProgress?.total || 0}</span>
                    </div>
                    <div className="bg-gray-200 rounded-full h-3 mb-4">
                      <div 
                        className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${(creationProgress?.total && typeof creationProgress?.completed === 'number') ? (creationProgress.completed / creationProgress.total) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-500">
                      {(creationProgress?.total && typeof creationProgress?.completed === 'number') ? Math.round((creationProgress.completed / creationProgress.total) * 100) : 0}% completado
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Asignar Meta a Usuarios</h2>
                <button
                  onClick={() => setShowUserSelectionModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>
              <div className="mt-4 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-blue-500 rounded-lg">
                    <FaBullseye className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-blue-900">Meta a Asignar</h3>
                </div>
                <div className="bg-white rounded-lg p-4 mb-4">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{currentGoalData.title}</h4>
                  <p className="text-gray-700 leading-relaxed">{currentGoalData.description}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="bg-white rounded-lg p-3">
                    <div className="flex items-center space-x-2 mb-1">
                      <FaCalendarAlt className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-gray-700">Fecha límite:</span>
                    </div>
                    <div className="text-gray-900 font-semibold">
                      {new Date(currentGoalData.targetDate).toLocaleDateString('es-ES', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <div className="flex items-center space-x-2 mb-1">
                      <FaStar className="w-4 h-4 text-yellow-600" />
                      <span className="font-medium text-gray-700">Prioridad:</span>
                    </div>
                    <div className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${
                      currentGoalData.priority === 'high' ? 'bg-red-100 text-red-800' :
                      currentGoalData.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {currentGoalData.priority === 'high' ? '🔴 Alta' :
                       currentGoalData.priority === 'medium' ? '🟡 Media' : '🟢 Baja'}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <div className="flex items-center space-x-2 mb-1">
                      <FaRocket className="w-4 h-4 text-purple-600" />
                      <span className="font-medium text-gray-700">Tiempo restante:</span>
                    </div>
                    <div className="text-gray-900 font-semibold">
                      {Math.ceil((new Date(currentGoalData.targetDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))} días
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Seleccionar Usuarios ({selectedUsersForGoal.length} seleccionados)
                  </h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={selectAllUsers}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      Seleccionar Todos
                    </button>
                    <button
                      onClick={() => {
                        const teacherIds = users
                          .filter(u => u !== undefined && u !== null && u.role === 'teacher') // 🛡️ Filtrar elementos undefined/null
                          .map(u => getUserId(u));
                        setSelectedUsersForGoal(teacherIds);
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      Solo Profesores
                    </button>
                    <button
                      onClick={deselectAllUsers}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors text-sm"
                    >
                      Deseleccionar Todos
                    </button>
                  </div>
                </div>
                
                <div className="grid gap-3 max-h-96 overflow-y-auto">
                  {users.map(user => {
                    const userId = getUserId(user);
                    const isSelected = selectedUsersForGoal.includes(userId);
                    
                    return (
                      <div
                        key={userId}
                        className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                          isSelected 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => toggleUserSelection(userId)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleUserSelection(userId)}
                              className="w-4 h-4 text-blue-600 rounded"
                            />
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                              {(user.firstName?.[0] || user.username?.[0] || 'U').toUpperCase()}
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900">
                                {user.firstName} {user.lastName}
                              </div>
                              <div className="text-sm text-gray-500">
                                {user.username} • {user.role}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-600">
                              {user.allowedCourses?.length || 0} cursos asignados
                            </div>
                            <div className={`text-xs px-2 py-1 rounded-full ${
                              user.isActive !== false 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {user.isActive !== false ? 'Activo' : 'Inactivo'}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  {selectedUsersForGoal.length > 0 
                    ? `${selectedUsersForGoal.length} usuario(s) seleccionado(s)`
                    : 'No hay usuarios seleccionados'
                  }
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowUserSelectionModal(false)}
                    disabled={creationProgress !== null}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => {
                      if (!currentGoalData) {
                        showError('No hay datos de meta para asignar');
                        return;
                      }
                      console.log('🎯 Asignando meta desde modal de selección:', currentGoalData);
                      createGoalForSelectedUsers(currentGoalData);
                    }}
                    disabled={selectedUsersForGoal.length === 0 || creationProgress !== null || !currentGoalData}
                    className={`px-6 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                      selectedUsersForGoal.length > 0 && !creationProgress && currentGoalData
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {creationProgress ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        <span>Creando...</span>
                      </>
                    ) : (
                      <span>Asignar Meta ({selectedUsersForGoal.length})</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
              )}

        {/* Modal de Edición de Meta */}
        {showEditGoalModal && selectedGoal && selectedGoalUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-700 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                      <FaEdit className="w-8 h-8" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold">✏️ Editar Meta</h2>
                                             <p className="text-blue-100 font-medium">
                         Modificar meta de {selectedGoalUser?.firstName} {selectedGoalUser?.lastName}
                       </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowEditGoalModal(false)}
                    className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition-colors"
                  >
                    <FaTimes className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-8 space-y-6">
                {/* Información del usuario */}
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                      {(selectedGoalUser?.firstName?.[0] || 'U').toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {selectedGoalUser?.firstName} {selectedGoalUser?.lastName}
                      </h3>
                      <p className="text-gray-600">
                        {selectedGoalUser?.username} • {selectedGoalUser?.role === 'admin' ? '👑 Administrador' : '👨‍🏫 Profesor'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Formulario de edición */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Título */}
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      🎯 Título de la Meta *
                    </label>
                    <input
                      type="text"
                      value={editGoalFormData.title}
                      onChange={(e) => setEditGoalFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium text-lg"
                      placeholder="Ej: Alcanzar 90% de evaluaciones completadas este mes"
                    />
                  </div>

                  {/* Fechas */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      📅 Fecha de Inicio
                    </label>
                    <input
                      type="date"
                      value={editGoalFormData.startDate}
                      onChange={(e) => setEditGoalFormData(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      🏁 Fecha Límite *
                    </label>
                    <input
                      type="date"
                      value={editGoalFormData.targetDate}
                      onChange={(e) => setEditGoalFormData(prev => ({ ...prev, targetDate: e.target.value }))}
                      min={editGoalFormData.startDate || new Date().toISOString().split('T')[0]}
                      className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    />
                  </div>

                  {/* Prioridad */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      ⚡ Prioridad
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { value: 'low', label: 'Baja', icon: '🟢', color: 'border-green-200 bg-green-50' },
                        { value: 'medium', label: 'Media', icon: '🟡', color: 'border-yellow-200 bg-yellow-50' },
                        { value: 'high', label: 'Alta', icon: '🔴', color: 'border-red-200 bg-red-50' }
                      ].map((priority) => (
                        <label
                          key={priority.value}
                          className={`border-2 rounded-xl p-3 cursor-pointer transition-all ${
                            editGoalFormData.priority === priority.value 
                              ? `${priority.color} border-current ring-4 ring-opacity-20` 
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="priority"
                            value={priority.value}
                            checked={editGoalFormData.priority === priority.value}
                            onChange={(e) => setEditGoalFormData(prev => ({ 
                              ...prev, 
                              priority: e.target.value as 'low' | 'medium' | 'high' 
                            }))}
                            className="sr-only"
                          />
                          <div className="text-center">
                            <div className="text-xl mb-1">{priority.icon}</div>
                            <div className="font-bold text-sm">{priority.label}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Estado */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      📊 Estado Actual
                    </label>
                    <select
                      value={editGoalFormData.status}
                      onChange={(e) => setEditGoalFormData(prev => ({ 
                        ...prev, 
                        status: e.target.value as 'pending' | 'in_progress' | 'completed' | 'overdue' 
                      }))}
                      className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    >
                      <option value="pending">⏳ Pendiente</option>
                      <option value="in_progress">🔄 En Progreso</option>
                      <option value="completed">✅ Completada</option>
                      <option value="overdue">⚠️ Vencida</option>
                    </select>
                  </div>

                  {/* Progreso */}
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      📈 Progreso: {editGoalFormData.progress}%
                    </label>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={editGoalFormData.progress}
                        onChange={(e) => setEditGoalFormData(prev => ({ 
                          ...prev, 
                          progress: parseInt(e.target.value) 
                        }))}
                        className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${editGoalFormData.progress}%, #e5e7eb ${editGoalFormData.progress}%, #e5e7eb 100%)`
                        }}
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-2">
                        <span>0%</span>
                        <span>25%</span>
                        <span>50%</span>
                        <span>75%</span>
                        <span>100%</span>
                      </div>
                      {/* Botones rápidos de progreso */}
                      <div className="flex justify-center space-x-2 mt-4">
                        {[0, 25, 50, 75, 100].map(value => (
                          <button
                            key={value}
                            onClick={() => setEditGoalFormData(prev => ({ ...prev, progress: value }))}
                            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                              editGoalFormData.progress === value
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            {value}%
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Descripción */}
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      📝 Descripción Detallada
                    </label>
                    <textarea
                      value={editGoalFormData.description}
                      onChange={(e) => setEditGoalFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all h-32 resize-none"
                      placeholder="Describe los objetivos específicos, beneficios esperados y criterios de éxito..."
                    />
                  </div>
                </div>

                {/* Vista previa de cambios */}
                <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-bold text-purple-900 mb-4">🔍 Vista Previa de Cambios</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="bg-white rounded-lg p-3">
                      <div className="text-purple-600 font-medium mb-1">Duración</div>
                      <div className="text-gray-900">
                        {editGoalFormData.startDate && editGoalFormData.targetDate
                          ? `${Math.ceil((new Date(editGoalFormData.targetDate).getTime() - new Date(editGoalFormData.startDate).getTime()) / (1000 * 60 * 60 * 24))} días`
                          : 'Sin fecha de inicio'
                        }
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <div className="text-purple-600 font-medium mb-1">Estado</div>
                      <div className="text-gray-900">
                        {editGoalFormData.status === 'pending' ? '⏳ Pendiente' :
                         editGoalFormData.status === 'in_progress' ? '🔄 En Progreso' :
                         editGoalFormData.status === 'completed' ? '✅ Completada' : '⚠️ Vencida'}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <div className="text-purple-600 font-medium mb-1">Completado</div>
                      <div className="text-gray-900 font-bold">{editGoalFormData.progress}%</div>
                    </div>
                  </div>
                </div>

                {/* Botones de acción */}
                <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setShowEditGoalModal(false)}
                    disabled={loading}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancelar
                  </button>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        // Completar meta rápidamente
                        setEditGoalFormData(prev => ({ 
                          ...prev, 
                          progress: 100, 
                          status: 'completed' 
                        }));
                      }}
                      disabled={loading || editGoalFormData.status === 'completed'}
                      className={`px-6 py-3 rounded-xl font-medium transition-colors flex items-center space-x-2 ${
                        editGoalFormData.status === 'completed'
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                    >
                      <FaCheck className="w-4 h-4" />
                      <span>Marcar Completada</span>
                    </button>
                    
                    <button
                      onClick={handleSaveEditedGoal}
                      disabled={loading || !editGoalFormData.title.trim() || !editGoalFormData.targetDate}
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-3 rounded-xl font-bold transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {loading && <FaSpinner className="animate-spin" />}
                      <FaStar className="w-4 h-4" />
                      <span>Guardar Cambios</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Eliminación Masiva de Metas */}
        {showBulkDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto">
              {/* Header */}
              <div className="bg-gradient-to-r from-red-600 via-red-700 to-red-800 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                      <FaTrash className="w-8 h-8" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold">Eliminación Masiva de Metas</h2>
                      <p className="text-red-100 font-medium">
                        Selecciona usuarios para eliminar TODAS sus metas
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowBulkDeleteModal(false)}
                    className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition-all"
                  >
                    <FaTimes className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {bulkDeleteProgress ? (
                <div className="p-8 space-y-6">
                  <div className="text-center">
                    <FaSpinner className="w-16 h-16 text-red-600 mx-auto mb-4 animate-spin" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Eliminando Metas...</h3>
                    <p className="text-gray-600 mb-6">
                      Procesando: {bulkDeleteProgress?.currentUser}
                    </p>
                    
                    <div className="bg-gray-200 rounded-full h-4 mb-4">
                      <div 
                        className="bg-red-600 h-4 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${(bulkDeleteProgress && bulkDeleteProgress.total && bulkDeleteProgress.total > 0 && typeof bulkDeleteProgress.completed === 'number') ? (bulkDeleteProgress.completed / bulkDeleteProgress.total) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-500">
                      {bulkDeleteProgress?.completed} de {bulkDeleteProgress?.total} usuarios procesados
                    </p>
                  </div>
                </div>
              ) : (
                <div className="p-6 space-y-6">
                  {/* Controles de selección */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <FaExclamationTriangle className="w-5 h-5 text-yellow-600" />
                      <span className="font-bold text-yellow-800">¡ATENCIÓN!</span>
                    </div>
                    <p className="text-yellow-700 text-sm">
                      Esta acción eliminará <strong>TODAS las metas</strong> de los usuarios seleccionados.
                      Una vez confirmada, <strong>no se puede deshacer</strong>.
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <h3 className="text-xl font-bold text-gray-900">
                        Seleccionar Usuarios ({usersSelectedForBulkDelete.length} seleccionados)
                      </h3>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={selectAllUsersForBulkDelete}
                          className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          Seleccionar Todos
                        </button>
                        <button
                          onClick={clearBulkDeleteSelection}
                          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          Limpiar Selección
                        </button>
                      </div>
                    </div>
                    
                    {usersSelectedForBulkDelete.length > 0 && (
                      <div className="text-right">
                        <div className="text-sm text-gray-600">
                          Metas a eliminar: {users.filter(u => usersSelectedForBulkDelete.includes(getUserId(u))).reduce((sum, user) => sum + (user.goals?.length || 0), 0)}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Lista de usuarios */}
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {users.map(user => {
                      const isSelected = usersSelectedForBulkDelete.includes(getUserId(user));
                      const userGoals = user.goals || [];
                      
                      return (
                        <div 
                          key={getUserId(user)}
                          onClick={() => toggleUserForBulkDelete(getUserId(user))}
                          className={`cursor-pointer rounded-xl p-4 border-2 transition-all duration-200 ${
                            isSelected 
                              ? 'border-red-500 bg-red-50' 
                              : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className={`w-4 h-4 rounded border-2 transition-all ${
                                isSelected 
                                  ? 'bg-red-500 border-red-500' 
                                  : 'border-gray-300'
                              }`}>
                                {isSelected && <FaCheck className="w-3 h-3 text-white" />}
                              </div>
                              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                                {(user.firstName?.[0] || user.username?.[0] || 'U').toUpperCase()}
                              </div>
                              <div>
                                <div className="font-semibold text-gray-900">
                                  {user.firstName} {user.lastName}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {user.username} • {userGoals.length} metas
                                </div>
                              </div>
                            </div>
                            
                            <div className="text-right">
                              <div className={`text-lg font-bold ${userGoals.length > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                {userGoals.length}
                              </div>
                              <div className="text-sm text-gray-500">
                                {userGoals.length === 1 ? 'meta' : 'metas'}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Botones de acción */}
                  <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    <button
                      onClick={() => setShowBulkDeleteModal(false)}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-medium transition-colors"
                    >
                      Cancelar
                    </button>
                    
                    <button
                      onClick={handleBulkDeleteAllGoals}
                      disabled={usersSelectedForBulkDelete.length === 0}
                      className={`px-6 py-3 rounded-xl font-bold transition-all flex items-center space-x-2 ${
                        usersSelectedForBulkDelete.length === 0
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-red-600 hover:bg-red-700 text-white transform hover:scale-105'
                      }`}
                    >
                      <FaTrash className="w-5 h-5" />
                      <span>
                        Eliminar {users.filter(u => usersSelectedForBulkDelete.includes(getUserId(u))).reduce((sum, user) => sum + (user.goals?.length || 0), 0)} Metas
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}



      {/* Success/Error Messages */}
      {success && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          <div className="flex items-center space-x-2">
            <FaCheck className="w-4 h-4" />
            <span>{success}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          <div className="flex items-center space-x-2">
            <FaTimes className="w-4 h-4" />
            <span>{error}</span>
          </div>
        </div>
      )}
    </div>
  );
  };

export default UserManagement; 